/**
 * QuoteBuilderPage — The assembled Quote Builder.
 *
 * Composes all Phase A3-A7 components into a working flow:
 * - QuoteHeader (customer, contact, addresses, salesperson, job type, payment)
 * - Line Items section (AreaCards grouped by area)
 * - AddItemCascade (Slideover wizard)
 * - EstimatePanel (sticky right-rail)
 * - All modals (edit, custom, alternate, option, tax, note, accessories)
 * - Auto-save (Q-2)
 * - Navigation to Review & Send
 *
 * Routes: /quotes/add (new) and /quotes/:id/edit (existing)
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { resolveBack } from '../../../utils/backOrigin';
import { computeReviewSendTarget } from '../../../quote-builder/reviewSendGuard';
import { groupLinesByArea } from '../../../quote-builder/groupLinesByArea';
// IDI-309: GM% / Bid Subtotal edits re-price eligible lines once, to hit the
// whole-quote target. Replaces the overall_margin write (and its IDI-420
// clampMarginPct guard: nothing user-typed reaches overallMargin any more).
import { distributeToTarget, targetEditability as computeTargetEditability } from '../../../quote-builder/distributeToTarget';
import { PlusIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import { ChartBarIcon, ClipboardDocumentListIcon, ArrowUturnLeftIcon, BriefcaseIcon, TrashIcon, ArrowDownTrayIcon, PaperAirplaneIcon, XCircleIcon, ArchiveBoxIcon } from '@heroicons/react/24/outline';
import MarkAsLostModal from '../MarkAsLostModal';
import { AttachModal } from '../../attachments/AttachModal';
import FilePreviewModal from '../../attachments/FilePreviewModal';
import { Button } from '../../ui/Button';
import { Card } from '../../ui/Card';
import { Alert } from '../../ui/Alert';
import { EmptyState } from '../../ui/EmptyState';
import { Dialog, DialogHeader, DialogBody, DialogFooter } from '../../ui/Dialog';
import PageHeader from '../../ui/PageHeader';
import DropdownMenu from '../../ui/DropdownMenu';
import StatusPill from '../../ui/StatusPill';
import Tabs from '../../ui/Tabs';
import { formatQuoteNumber } from '../../../utils/displayId';
import { toDisplayString } from '../../../utils/displayString';
import { useToast } from '../../ui/Toast';
import { NotesPanel } from '../../ui/NotesPanel';
import { useConfirm } from '../../ui/ConfirmationModal';
import { settingsAPI } from '../../../services/api';
import api from '../../../services/api';
import { entryDisplay, attachmentIdOf, mergeDocumentEntries, removeDocumentById } from '../../../utils/attachmentEntry';
import { lastSendAttachments, canIncludeInPdf, pdfRefusalMessage } from '../../../utils/quotePdfFiles';
// IDI-208/IDI-136: reuse the pricebook+overlay merge Settings already uses
// (IDI-9, #115) instead of re-deriving it here. Shared module (not
// QuoteItemsPage.js directly) so this bundle doesn't pull in the Settings
// page component tree.
import useBuilderCatalog from '../../../hooks/useBuilderCatalog';

// Builder components
import QuoteHeader from './QuoteHeader';
import AddItemCascade from './AddItemCascade';
import AreaCard from './AreaCard';
import useDragReorder from '../../../hooks/useDragReorder';
import useSalespersonOptions from '../../../hooks/useSalespersonOptions';
import EstimatePanel from './EstimatePanel';
import { CustomItemModal, AddAlternateModal, TaxModal, NoteModal } from './modals';
import { ChooseTemplateModal, SaveTemplateModal } from './TemplateModals';
import QuoteAnalysisReport from './QuoteAnalysisReport';
import MaterialListDialog from './MaterialListDialog';
import { applyLineEdit } from '../../../quote-builder/applyLineEdit';
import { resolveLoadedTaxRate, pickSettingsTaxRate, describeTaxRate } from '../../../quote-builder/resolveLoadedTaxRate';

import ProjectDetailsCard from './ProjectDetailsCard';
import AcknowledgmentsPanel from '../../acknowledgments/AcknowledgmentsPanel';

// Quote-builder module
import {
  useQuoteMargins,
  useQuoteAutoSave,
  emptyPrefill,
  assembleQuotePayload,
  shouldActivateOnSave,
  quotesAPI,
  projectAccessoriesToMaterial,
  prefillFromCustomer,
  resolveDisplayStatus,
  getAvailableActions,
  getQuoteLockState,
  quoteLockLabel,
  normalizeMarginValue,
  resolveLoadedLineItems,
  stripTemplateMargin,
} from '../../../quote-builder';
import rescaleLineQuantity from '../../../quote-builder/rescaleLineQuantity';
import { reopenConfirmOptions } from '../../../quote-builder/quoteLifecycle';
import { normalizeInternalNotes, sortNotesNewestFirst, noteActionErrorMessage } from '../../../quote-builder/quoteInternalNotes';
import { useOptionalAuth } from '../../../contexts/AuthContext';
import repriceOpenQuoteLines from '../../../quote-builder/repriceOpenQuoteLines';
import { rederiveCategoryMargins } from '../../../quote-builder/seedCatalog';
import { formatCurrency } from '../../../utils/formatCurrency';

// ─── Component ───────────────────────────────────────────────────────────────

// PR-18 (2026-07-22): format the single muted meta line under the page title.
// Wire QR-1 R5 shape: e.g. "Not sent · Last edited 2 days ago by Dana R."
function formatHeaderMeta({ updatedAt, updatedByName } = {}) {
  if (!updatedAt) return undefined;
  const rel = relativeTime(updatedAt);
  const suffix = updatedByName ? ` by ${updatedByName}` : '';
  return rel ? `Last edited ${rel}${suffix}` : undefined;
}

// IDI-237 (2026-09-03): the absent-value guard.
//
// Portica measured on quote 48202 + 19900: header rendered "Last edited 690
// months ago" (57½ years). Root cause: `Date.parse(iso)` + `Number.isNaN(t)`
// misses THREE cases:
//   1. iso is the epoch string ("1970-01-01T00:00:00Z") → Date.parse returns
//      exactly 0, not NaN, so the NaN check passes and the formatter renders
//      "690 months ago" against now(). Same class of defect as the
//      "1970-01-01 customer dates" formatDate.js:22-24 was written against.
//   2. iso is an empty string "" → in some browsers (Firefox) Date.parse("")
//      returns 0 not NaN; in Chrome it returns NaN. Cross-browser hazard.
//   3. iso is a Date object (rare but legitimate) with getTime() === 0.
//
// Guard shape mirrors formatDate.js:44-65 (the canonical toDate helper).
// Not exporting toDate from formatDate.js — its own docblock says
// "Internal helper — do not export." Duplicating the guard mechanism is
// permitted; duplicating the format logic is not (the file's line 9-14).
//
// 🛑 THIS GUARD IS A STOPGAP — Portica paste 78, IDI-207.
//
// The 1970-01-01 values are a DATA-QUALITY defect (migration residue), not
// a rendering defect. Portica measured 38 of 500 quotes (7.6%) and 413 of
// 607 customers on tenant 270 carry updatedAt = 1970-01-01T00:00:00Z. The
// real root cause is IDI-207 (migration writes epoch when the source has
// no updatedAt).
//
// DELETION TRIGGER: when IDI-207's migration lands and the LIST census
// shows zero pre-2020 timestamps on any tenant's updatedAt/createdAt/
// convertedAt fields, remove the APP_EPOCH_MS gate below. The falsifier
// test in QuoteBuilderPage.idi237-relative-time-guard.test.js has a
// meta-test naming the exit condition; a grep for "IDI-207 STOPGAP" in
// this file finds the two lines to change.
//
// Do NOT remove the absent-value guard (null/undefined/"") or the
// isFinite check — those are permanent (Firefox Date.parse("") = 0 is a
// browser behaviour, not a data defect). Only the APP_EPOCH_MS threshold
// is IDI-207-conditional.
//
// APP_EPOCH_MS threshold rationale (Portica paste 78 census): earliest
// legitimate value on tenant 270 is 2023-02-28. 2020-01-01 has 3+ years
// of headroom and cannot blank a real value. Do NOT tighten to the
// tenant's createdAt — the extra precision buys nothing and couples a
// display guard to a fetch.
//
// Falsifier: for any iso value where relativeTime returns non-null, the
// returned string must be plausibly a recent time (not 57 years ago).
//
// 🔎 DISCRIMINATING QUESTION resolved (Portica paste 78, source-read at
// 1130426): when relativeTime returns null, formatHeaderMeta returns
// undefined, and PageHeader:84 gates the ENTIRE meta block on
// `{(meta || subtitle) && (...)}`. The whole "Last edited ..." line is
// SUPPRESSED. No dangling label. No em-dash-family regression.
// See QuoteBuilderPage.idi237-relative-time-guard.test.js's
// formatHeaderMeta composition test.
function relativeTime(iso) {
  // formatDate.js:44-65 absent-value guard.
  if (iso === null || iso === undefined || iso === '') return null;
  const t = iso instanceof Date ? iso.getTime() : Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  // 🛑 IDI-207 STOPGAP — delete these two lines when IDI-207's migration
  // lands. Grep for "IDI-207 STOPGAP" in this file finds every line to
  // change. Rationale, threshold justification, and deletion condition
  // in the docblock above.
  const APP_EPOCH_MS = Date.parse('2020-01-01T00:00:00Z');
  if (t < APP_EPOCH_MS) return null;
  const diffMs = Date.now() - t;
  // Future-dated: also reject. A "last edited" cannot be in the future;
  // clock skew or a bad record produces this. Threshold is 1 hour in the
  // future to allow for minor client-server skew.
  if (diffMs < -3600000) return null;
  const min = Math.round(diffMs / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} minute${min === 1 ? '' : 's'} ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day} day${day === 1 ? '' : 's'} ago`;
  const mo = Math.round(day / 30);
  return `${mo} month${mo === 1 ? '' : 's'} ago`;
}

// IDI-209: Quote builder tabs
const QUOTE_TABS = [
  { value: 'details',          label: 'Project Details' },
  { value: 'quote',            label: 'Quote Detail' },
  { value: 'notes',            label: 'Notes' },
  { value: 'files',            label: 'Files' },
  // IDI-540: Acknowledgments tab (label per IDI-164). The 'acknowledgements'
  // value is the ?tab= deep-link key and must not change.
  { value: 'acknowledgements', label: 'Acknowledgments' },
];

// IDI-398: a signed quote's lines, customer, terms and pricing are read-only
// (ruled 2026-10-05 / D-01). `inert` blocks every control inside the section,
// custom buttons and menus included, plus keyboard focus; a disabled
// <fieldset> only reaches native inputs, which most of the builder isn't.
// React 18 passes unknown attributes through, so `inert=""` reaches the DOM.
function lockedSectionProps(locked) {
  return locked
    ? { inert: '', 'aria-disabled': 'true', className: 'contents opacity-75', title: 'Signed — locked' }
    : { className: 'contents' };
}

export default function QuoteBuilderPage() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = searchParams.get('tab') || 'details';
  const handleTabChange = (tab) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    }, { replace: true });
  };
  const navigate = useNavigate();
  // PR-02e: canonical Toast API shape is { show, success, info, warning, error }.
  // Prior `const { toast } = useToast()` destructured undefined — silent no-op.
  const toast = useToast();
  const confirm = useConfirm();
  // IDI-552: the signed-in user's Cognito sub — a note's author sees edit/delete.
  const currentUserId = useOptionalAuth()?.user?.id || null;
  const isEditing = !!id;

  // ─── Iter18 R2 F.3: Template full-edit round-trip ──────────────────────
  // When the builder is opened from Settings › Quote Templates via
  //   /quotes/add?editing_template_id=<id>
  // we (a) load that template's line items into the builder, (b) show an
  // "Editing Template: {name}" chip in the header, (c) rename the primary
  // Finalize CTA to "Save Template", and (d) on save call
  // settingsAPI.updateQuoteTemplate() then navigate back to the settings list.
  //
  // Ships with the header-chip pattern + finalizeLabel prop plumbing that
  // Change Orders (Iter19+) will re-use as the "golden template" for the
  // family of quote-shaped editors (per plan §1.7 Q3).
  const editingTemplateId = searchParams.get('editing_template_id') || null;
  const isTemplateEdit = !!editingTemplateId;
  const location = useLocation();
  // IDI-157: origin for Back, captured once at mount (the ?tab= sync
  // navigates without state).
  const [quoteBack] = useState(() => resolveBack(location.state, { href: '/quotes', label: 'Back to Quotes' }));
  const [editingTemplate, setEditingTemplate] = useState(null);

  // ─── Core State ────────────────────────────────────────────────────────────

  const [prefill, setPrefill] = useState(emptyPrefill());
  const [metadata, setMetadata] = useState({ title: '', notes: '', validityDays: 30 });
  // IDI-305: internal notes are an append-only, attributed list, held apart
  // from metadata so they never ride the autosave payload. Written only by
  // quotesAPI.addQuoteNote (server stamps author + time from the login).
  const [internalNotesList, setInternalNotesList] = useState([]);
  const [lineItems, setLineItems] = useState([]);
  // DEF-152 (2026-08-04): initial tax rate for NEW quotes.
  // D-231 ruling: don't substitute a jurisdiction constant when no Settings
  // value exists. The reader (buildViewQuote:171) uses `tax_rate ?? 0`;
  // the writer must match. A new quote starts at 0% (no tax) until the
  // user explicitly sets a rate via the Tax modal. The tenant's configured
  // rate should eventually seed this from Settings/Tax & Rates (DEF-152
  // follow-up), but a hardcoded 0.07 is not that.
  const [taxRate, setTaxRate] = useState(0);
  // IDI-380: a genuine tax EXEMPTION is distinct from "no rate set yet".
  // Before this, taxExempt was derived as `taxRate === 0`, so every quote
  // with no rate loaded falsely read as Tax Exempt. Track a real flag that is
  // true only when the user (or a loaded quote) explicitly marks it exempt.
  const [taxExempt, setTaxExempt] = useState(false);
  // IDI-668 (D3): 'override' once the user changes a non-exempt rate in Edit
  // Tax (0% too); automatic rates follow the job site's state (D1/D2).
  const [taxSource, setTaxSource] = useState('configured');
  // IDI-668: the dealer's Tax & Rates rows (null until fetched).
  const [taxRows, setTaxRows] = useState(null);
  // The site state tax was last resolved for; undefined until seeded.
  const taxSiteRef = useRef(undefined);
  // IDI-668 (D6): a customer picked before Settings arrived.
  const earlyPickRef = useRef(null);
  // PT-31 (2026-07-27): overallMargin is now settable to support the
  // Estimate Panel's editable gross-margin / bid-subtotal round-trip. Both
  // paths route through `setOverallMargin` — direct edit sets it verbatim;
  // subtotal edit back-calculates the margin via `marginFromPrice`.
  // §1b final (2026-08-07): overallMargin is the LOWEST tier in the
  // precedence chain — a fallback for lines with no category margin. It is
  // never applied as a flat display override. See EstimatePanel for detail.
  // IDI-534: no hardcoded 35% start value. Null until Settings (or the
  // loaded document) supply one; the engine treats unset as no margin.
  const [overallMargin, setOverallMargin] = useState(null);
  // IDI-205: account-wide labor burden %, applied live to every quote's Labor
  // cost bucket (NOT a one-time prefill — unlike payment_term/payment_type,
  // this must recompute on every render, so it's threaded into costBreakdown
  // via useQuoteMargins/computeQuote, not just seeded onto a form field).
  // Stored as a 0-100 percent on Settings → Quote Defaults.
  // IDI-457 (parent IDI-455): the catalog + pricing inputs come from the ONE
  // shared loader, so the change-order builder offers the same real
  // pricebook and prices with the same inputs this page uses.
  //
  // What the hook owns: the pricebook/overlay merge (IDI-208/136), the two
  // offerable subsets (IDI-373), categoryMargins, laborDefaults,
  // laborBurdenPct (IDI-205) and customerTypes (IDI-379).
  //
  // What stays QUOTE-ONLY, below: the `master_default_margin` seed gated on
  // `!isEditing && !isTemplateEdit` (IDI-47 — Settings is cached and fast
  // while the quote fetch is slower, so on an edit page seeding would
  // overwrite the quote's own margin; 61 quotes were contaminated live by
  // that race), the new-quote tax-rate seed, the payment defaults and the
  // `?customerId` prefill. They read the SAME `catalogSettings` object the
  // hook already fetched, so there is still only one settings round-trip.
  //
  // `accessoryCatalog` is the offerable accessory subset; `catalogRowsAll` is
  // the UNFILTERED merge (IDI-105: a line already on the quote still needs
  // its Qty-on-Hand figure even if the product is no longer offerable);
  // `insulationCatalog` is the area-priceable subset the composite builder
  // merges with the static PRODUCTS[].
  const {
    accessoryCatalog,
    catalogRows: catalogRowsAll,
    insulationCatalog,
    categoryMargins,
    laborDefaults: tenantLaborDefaults,
    laborBurdenPct,
    customerTypes,
    settings: catalogSettings,
    catalogLoaded,
    appendAccessory,
  } = useBuilderCatalog();
  // IDI-534: the loader reads the latest Settings category margins from this
  // ref (its effect doesn't re-run on catalog changes).
  const categoryMarginsRef = useRef(categoryMargins);
  categoryMarginsRef.current = categoryMargins;
  const [marginUserEdited] = useState(false); // PR-22; IDI-309: the estimate card no longer sets this (it re-prices lines instead of writing overall_margin)
  const [loadedOverallMargin, setLoadedOverallMargin] = useState(null); // §8: preserve existing value on full-replace save
  // PT-30 (2026-07-26): hydrate the customer-types list from Settings so the
  // engine can resolve Lindsey level-3 `customerTypeMargin` for the currently-
  // selected customer. Shape: [{ value, label, default_margin_pct?, is_default?, ... }].
  // See docs/pt-30/PRECEDENCE.md.
  // IDI-457: `customerTypes` now comes from useBuilderCatalog (IDI-379: the
  // list lives under Settings' camelCase `customerTypes` key).
  // R20-9 (2026-08-04): wire the salesperson options hook so the picker
  // renders the tenant's user list instead of an empty array.
  const { options: salespersonOptions } = useSalespersonOptions();
  const [discounts, setDiscounts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [quoteAnalysisOpen, setQuoteAnalysisOpen] = useState(false);
  // IDI-105: More -> Material List.
  const [materialListOpen, setMaterialListOpen] = useState(false);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  // IDI-289 (2026-09-09): quote documents for the Files tab.
  // Canonical key is `documents[]` (cmd/quotes/attachments.go:496-521).
  // Populated from the quote GET response; updated optimistically on
  // attach (via AttachModal's onAttached) and delete.
  const [quoteDocuments, setQuoteDocuments] = useState([]);
  // NOTE (PR-A, 2026-07-21): `quoteOptions` state slot retired — options are
  // written to `line_items[]` with `areaLabel: 'Options'` via handleAddItem
  // (AddItemCascade → Options area). See tech-debt ledger "Builder quoteOptions state is
  // dead code" and plan `context/plans/tech-debt-cleanup-post-pr4.md` §1 A.2.
  // `assembleQuotePayload` still emits `quote_options: []` as future-safe
  // defense in depth; `separateOptions` still recognises quote_options[] as a
  // source. When the backend PR (§3.B) introduces a first-class schema this
  // seam is where a fresh state slot re-enters.
  const [quoteId, setQuoteId] = useState(id || null);
  // IDI-332 (2026-09-14): provenance marker, NOT an equality check against
  // quoteId. When the first autosave creates a quote, we navigate the URL
  // from /quotes/add to /quotes/:id/edit so a reload doesn't orphan the
  // record (confirmed via two isolated mount-survival probes before this
  // fix: React Router does NOT remount QuoteBuilderPage across this
  // transition — same component instance, same tree position — so this
  // is safe from a "leaked component" perspective). But that URL change
  // flips `isEditing` (`!!id`) from false to true, which fires the
  // load-and-reset effect below (:596) — the SAME effect that exists
  // specifically to reset state when switching from quote A to quote B
  // (see its own comment). Without a guard, the fresh navigate would
  // reset all just-typed state and refetch from the server, discarding
  // anything typed between the save firing and the fetch returning —
  // exactly the failure mode already ruled unsafe on IDI-324 §2.
  // `justCreatedIdRef` records THIS SPECIFIC id as "we created it, we're
  // already looking at it" at the moment of creation. The effect checks
  // this ref (and clears it after one skip) rather than comparing
  // `id === quoteId`, because on a genuine hard reload quoteId is also
  // freshly seeded from the URL id (`useState(id || null)` above) —
  // `id === quoteId` would ALSO be true then, and skipping the load on a
  // real reload is exactly the bug this ticket exists to fix (the record
  // would never actually be fetched). The ref only survives WITHIN one
  // mounted session; a reload always starts with an empty ref, so the
  // reload path is unaffected and still fetches normally.
  const justCreatedIdRef = useRef(null);
  // PT-53A W3.1 (DEF-119): track whether the loaded quote is price-locked
  // (acknowledged). Used to strip price-bearing fields from auto-save payload.
  const [isQuoteLocked, setIsQuoteLocked] = useState(false);
  const [loading, setLoading] = useState(isEditing || isTemplateEdit);
  // PR-18 (2026-07-22): capture server-side status/last-edited fields so the
  // PageHeader can render the inline status badge + meta line pattern.
  const [quoteMeta, setQuoteMeta] = useState({ status: null, updatedAt: null, updatedByName: null });
  // PR-21: stored margin pair from the migrated record (canonical per 287_ §2)
  const [storedMarginPair, setStoredMarginPair] = useState(null);

  // IDI-280 (2026-09-14): tcInfoOpen/tcSelectorOpen/tcTemplates/handleChangeTC
  // removed — they existed solely to drive the "Change" button on the T&C
  // card, which is now removed (see the render-site comment above). This
  // whole chain (ChooseTCTemplateModal + the info Dialog + their state) was
  // left UNREACHABLE by the card removal — confirmed via `npx react-scripts
  // build` failing on no-unused-vars for handleChangeTC/NoteTemplateMenu
  // before this cleanup, not assumed. T&C selection now happens exclusively
  // on Review & Send (its own SelectMenu, durable via IDI-280/#423).
  // §2 (2026-08-08): Attach control was hardcoded disabled awaiting B2.
  // B2 shipped (2026-08-03). AttachModal now fully wired.
  const [attachOpen, setAttachOpen] = useState(false);
  // IDI-445: the Files-tab row whose file is open in the preview modal.
  const [previewFileKey, setPreviewFileKey] = useState(null);

  // PT-33a (2026-07-27): The former PT-14 C3 "Attach" stub is DISABLED
  // here in favour of the full attachment-picker build folding into B2. The
  // stub captured `{name, size}` into local state, never persisted anything,
  // AND wrote a shape (`{name, size}`) that neither the read side
  // (QuoteDetail.js:377 expected `att.filename`+`att.download_url`;
  // QuoteDocument.js:400 expected `{id, name, description}`) nor the backend
  // Quote struct accepted — four divergent shapes across one surface.
  //
  // Attachments will ship as part of B2 with a canonical shape (defined once,
  // read + written by every consumer). Until then the CTA is visibly
  // disabled with a 'coming soon' reason — no silent capture, no shape drift.
  // See PT-33a report + B2 spec update.

  // ─── Margin Engine ─────────────────────────────────────────────────────────

  // PR-02b §1b.2 + PR-02c bucket-fix: project accessory costs into the line's **Material**
  // bucket (not Other) so the margin engine + Cost Breakdown report accessories under Material.
  // See `projectAccessoriesToMaterial` for the bucket contract + LAT-A16 test.
  const lineItemsForMargins = useMemo(() => projectAccessoriesToMaterial(lineItems), [lineItems]);

  // PT-30 (2026-07-26): resolve the current customer's Lindsey level-3 margin
  // by looking up their `customerType` (captured by prefillFromCustomer at
  // customer-selection time) in the tenant's customer_types Settings list.
  // Returns null if either the type or its default_margin_pct is unset;
  // the engine cascade then falls through to `quoteMargin` (level 5).
  const customerTypeMargin = useMemo(() => {
    if (!prefill.customerType || !Array.isArray(customerTypes)) return null;
    const match = customerTypes.find((t) => t?.value === prefill.customerType);
    return normalizeMarginValue(match?.default_margin_pct);
  }, [prefill.customerType, customerTypes]);

  // PT-30 (2026-07-27, follow-up to 30.2 ruling): stable margin-context object
  // for the Add-Item wizard's committed-line preview. Same shape passed to
  // `useQuoteMargins` above so the wizard preview mirrors the Estimate Panel's
  // truth-of-record for the current customer. Threaded QuoteBuilderPage
  // → AddItemCascade → AddItemGroups → GroupCard → computeLineItem.
  // IDI-534 (G1): the dealer default, the last tier of the 10-02 order
  // (category → customer → customer type → dealer default). A reopened quote
  // with no stored overall_margin never got it (the IDI-47 seed below is
  // new-quote only), so a line with no other tier priced at cost. DERIVED,
  // never set into state: a stored margin still wins, nothing races the quote
  // fetch (IDI-47), and the payload's overall_margin rule keeps reading the
  // raw `overallMargin`, so the fallback is never written.
  const masterDefaultMargin = useMemo(() => normalizeMarginValue(
    catalogSettings?.marginDefaults?.master_default_margin
      ?? catalogSettings?.master_default_margin,
  ), [catalogSettings]);
  // IDI-652: the dealer's Quote Defaults term. Settings and the seed save
  // `payment_terms` (plural); the singular keys are legacy fallbacks (D3).
  const dealerDefaultPaymentTerm = useMemo(() => {
    const qd = catalogSettings?.quoteDefaults || catalogSettings?.quote_defaults || {};
    return qd.payment_terms || qd.payment_term || qd.paymentTerm || undefined;
  }, [catalogSettings]);
  const effectiveOverallMargin = overallMargin
    ?? (loadedOverallMargin == null ? masterDefaultMargin : null);

  const wizardMarginContext = useMemo(() => ({
    customerMargin: prefill.customerDefaultMargin ?? null,
    customerTypeMargin,
    quoteMargin: effectiveOverallMargin,
  }), [prefill.customerDefaultMargin, customerTypeMargin, effectiveOverallMargin]);

  // PR-12 (2026-07-22): thread `discounts` into the margin engine so the
  // Estimate rail's Bid Total actually reflects them. Was previously
  // computed on the pre-discount subtotal, meaning the rail total ignored
  // whatever the user entered in the discount modal.
  const { computedLines, updateLineMargin } = useQuoteMargins(
    lineItemsForMargins,
    {
      overallMargin: effectiveOverallMargin,
      taxRate,
      discounts,
      laborBurdenPct,
      // PT-30 (2026-07-26): thread Lindsey levels 2–3 into the engine so
      // customer-specific and customer-type defaults resolve correctly. Both
      // are null when not set — the engine cascades to `overallMargin`.
      customerMargin: prefill.customerDefaultMargin ?? null,
      customerTypeMargin,
      // §2 (RS-2, 2026-08-07): prefer stamped intent for locked quotes so the
      // builder displays exactly what the customer received. Drafts always
      // resolve fresh so a category-margin edit picks up immediately.
      isSent: isQuoteLocked,
    }
  );

  // PT-15 #2 (2026-07-24): base-bid summary excludes Options. We compute a
  // second summary from only the base line items so the EstimatePanel rail
  // shows the base bid total — mirroring Review & Send's `isOptionLine`
  // predicate (see QuoteReviewSend.js `separateOptions`). computedLines
  // itself still covers ALL lines so option groups can render their totals.
  //
  // Predicate shape (must match R&S's isOptionLine): a line is an option if
  // it carries an `optionId`, OR `is_option === true`, OR `type === 'option'`,
  // OR `kind === 'option'`, OR `areaLabel === 'Options'`. The AddItemCascade
  // tags option lines with `optionId` on commit; legacy quotes that used
  // `areaLabel: 'Options'` still fall through to the label check.
  const isOptionRow = (li) => (
    !!li?.optionId ||
    li?.is_option === true ||
    li?.type === 'option' ||
    li?.kind === 'option' ||
    li?.areaLabel === 'Options' ||
    li?.areaId === 'options'
  );
  const baseLineItemsForMargins = useMemo(
    () => lineItemsForMargins.filter((li) => !isOptionRow(li)),
    [lineItemsForMargins]
  );
  const { summary: baseSummary } = useQuoteMargins(
    baseLineItemsForMargins,
    {
      overallMargin: effectiveOverallMargin,
      taxRate,
      discounts,
      laborBurdenPct,
      // PT-30: same context as the primary engine call above.
      customerMargin: prefill.customerDefaultMargin ?? null,
      customerTypeMargin,
      // §2 (RS-2): same isSent gating as the primary call.
      isSent: isQuoteLocked,
    }
  );


  // ─── IDI-309 / IDI-361: estimate-card target edit ──────────────────────────
  // Gross Margin % and Bid Subtotal re-price the ELIGIBLE lines once, so the
  // base-bid subtotal lands on the typed figure. Eligible = non-Option, no
  // dealer-set category margin, not frozen (locked quote reads stamps; a
  // stamped line on a sent/signed/converted quote). The write is each line's
  // price (lineTotal); lineMargin / categoryMargin / overall_margin are never
  // written, so there is no standing override. Autosave persists the lines.
  const targetOpts = useMemo(() => ({
    quote: quoteMeta,
    preferStamp: isQuoteLocked,
    // Same base-bid predicate as baseSummary, so the fixed portion plus the
    // re-priced lines add up to exactly the subtotal the card shows.
    isIncluded: (li) => !isOptionRow(li),
  }), [quoteMeta, isQuoteLocked]); // eslint-disable-line react-hooks/exhaustive-deps
  const targetEditability = useMemo(
    () => computeTargetEditability(lineItems, targetOpts),
    [lineItems, targetOpts]
  );
  const handleApplyTarget = useCallback(({ subtotalCents }) => {
    const result = distributeToTarget(lineItems, subtotalCents, {
      ...targetOpts,
      // Measure with the SAME engine output the card sums (accessories are
      // projected into materialCost there), index-aligned with lineItems.
      measure: (_li, i) => computedLines[i] || { total: 0, cost: 0 },
    });
    if (result.ok && !result.unchanged) setLineItems(result.lines);
    return result;
  }, [lineItems, computedLines, targetOpts]);

  // PR-21 §4: Migrated-quote margin adapter.
  // Trigger: no line carries a readable margin (lineMargin / margin_pct).
  // When triggered, override the summary with the stored grossMargin + bidAmountCents pair.
  const migratedAdapterActive = useMemo(() => {
    if (!storedMarginPair) return false;
    // §4.1 (294_): a user-set overall margin disengages the adapter, exactly as
    // a line margin does. Protects the case where a dealer explicitly sets 0%.
    // §3 (295_): loadedOverallMargin is the post-reload equivalent of marginUserEdited —
    // after PR-22 + §9, overall_margin is only written when the user chose it, so its
    // presence on a record IS user intent.
    if (marginUserEdited || loadedOverallMargin != null) return false;
    // IDI-309: a line re-priced from the estimate card carries its own price;
    // the stored bid pair no longer describes this quote.
    if (lineItems.some((li) => li?.marginSource === 'price_override')) return false;
    // LIVE state: any line has a margin the engine can read → adapter OFF
    const hasLineMargin = lineItems.some(li =>
      li.lineMargin != null || Number(li.margin_pct) > 0 || Number(li.marginPct) > 0
    );
    if (hasLineMargin) return false;
    // Check for zero-cost lines (MIGRATED_NO_COSTS) — TODO, fall through to today's behaviour
    const allCostsZero = lineItems.length > 0 && lineItems.every(li =>
      (Number(li.materialCost || li.materialCostCents || 0) === 0) &&
      (Number(li.laborCost || li.laborCostCents || li.totalLaborCostCents || 0) === 0)
    );
    if (allCostsZero) return false; // TODO: MIGRATED_NO_COSTS — held for Adrian's ruling
    return true; // MIGRATED state
  }, [lineItems, storedMarginPair, marginUserEdited, loadedOverallMargin]);

  const effectiveSummary = useMemo(() => {
    if (!migratedAdapterActive || !storedMarginPair) {
      // Q-08 (§3ao, 2026-08-18): marginSource is a STABLE discriminant naming
      // which rule produced the number on screen. The bid-record adapter is
      // NOT engaged here, so the value comes from the quote engine
      // (line/category > customer > overall). See 00_OPERATOR_RULINGS §3ao
      // for the precedence including the bid record.
      return baseSummary
        ? { ...baseSummary, marginSource: 'quote-engine' }
        : baseSummary;
    }
    const { grossMargin: gm, bidAmountCents: bid } = storedMarginPair;
    const marginDollars = Math.round(bid * gm); // bid & margin are authoritative; cost is the odd one out
    return {
      ...baseSummary,
      blendedMargin: gm,
      bidSubtotal: bid,
      bidTotal: bid + (baseSummary?.taxAmount ?? 0),
      marginDollars,
      // Q-08 (§3ao): the migrated-bid-record adapter is engaged. This is the
      // authoritative source for THIS quote and it OVERRIDES the customer's
      // margin — a migrated quote keeps the margin it was actually bid at.
      marginSource: 'migrated-bid',
      _migratedAdapter: true, // retained for backwards compat with any other consumer; new code should read marginSource
    };
  }, [migratedAdapterActive, storedMarginPair, baseSummary]);

  // ─── Auto-Save (Q-2) ──────────────────────────────────────────────────────

  // PR-4 §1b: builder-local `discounts` are shipped in the payload. Before
  // this fix they lived only in local state and never reached the customer
  // document. See plan §1b + §2 for details.
  // PR-A retires the dead `quoteOptions` state slot; options live in
  // `line_items[]` with `areaLabel: 'Options'`. `assembleQuotePayload` still
  // emits `quote_options: []` for future-safe defense in depth.
  // PT-53 §53.3c (2026-07-30): thread `computedLines` (from useQuoteMargins)
  // into assembleQuotePayload so `serializeLineItem` can source the
  // authoritative price-per-line (`computed.total`) for normally-computed
  // rows. Prior to this the write side spread raw builder state; `lineTotal`
  // was only present when the user overrode price manually → downstream
  // readers saw `line_total: undefined` and rendered $0.00 (D-041/D-043).
  const quotePayload = useMemo(
    () => assembleQuotePayload(prefill, lineItems, {
      ...metadata,
      discounts,
      // §4 (2026-07-29): persist computed total so the landing table shows the correct amount.
      total_amount: baseSummary?.bidTotal ?? 0,
      // BP-2 (2026-08-04): persist the resolved tax rate so it stamps on
      // every save. When the quote is sent, this value is the rate that was
      // in effect — future Settings changes don't retroactively alter it.
      tax_rate: taxRate,
      // BP-2: tax_source records provenance for audit. Full cascade tracking
      // (location:MN vs default vs override) requires state plumbing deferred
      // to follow-up; for now record whether a rate is set or absent.
      tax_source: taxSource === 'override' && !taxExempt ? 'override' : (taxRate > 0 ? 'configured' : 'none'),
      // IDI-380: persist genuine exemption separately from a 0 rate, so a
      // reloaded quote distinguishes "exempt" from "no rate set".
      tax_exempt: taxExempt,
      // PT-53A W3.2 (DEF-124, DEC-03): persist the edited overallMargin so
      // it survives reload. Without this, editing bid subtotal or margin $
      // recomputes the UI but the save payload doesn't carry the new margin
      // → reload reverts to the Settings default (0.35).
      // PR-18 P0: only persist when > 0 (a 0% margin from the bug must not re-stamp).
      ...((marginUserEdited || loadedOverallMargin != null) && overallMargin != null && Number.isFinite(overallMargin) ? { overall_margin: overallMargin } : {}),
      // IDI-22 (2026-08-11 ruling, gate step): a derived total (recomputed
      // from line items, flagged total_source='derived_2026_08_11') must
      // clear the moment the dealer opens and saves the quote in the
      // builder — that save IS the review the ruling requires, and
      // total_amount above is now the real engine-computed total, not the
      // recompute-script's approximation. Explicit null so the backend's
      // generic merge (existing[k] = val) overwrites rather than skips it;
      // unconditional because every builder save recomputes total_amount
      // from real line items regardless of whether this quote ever carried
      // the flag (a no-op null-write when it didn't).
      total_source: null,
      total_derived_at: null,
      // IDI-313 (2026-09-10, ruled): activate a draft quote on the first
      // save that carries at least one line item. A brand-new quote is
      // created as `draft` (cmd/quotes/main.go:1107); nothing ever flips it
      // to `active` unless the user explicitly sends it or marks it unsent.
      // Convert to Job requires active/sent/won (main.go:1633) — a draft
      // quote with real line items could never become a job. This closes
      // that gap at the SOURCE of the write, not as a backend precondition
      // hop (which exists for send at main.go:2186-2192 but not for
      // convert_to_job).
      //
      // "First save" precision: gated on the IN-MEMORY status
      // (quoteMeta.status), not a network round-trip, so a half-entered
      // line during an in-flight debounce doesn't retrigger this — once
      // the local status flips to 'active' (onSaved below), this condition
      // stops firing for subsequent saves on the same session.
      ...(shouldActivateOnSave(quoteMeta.status, lineItems.length) ? { status: 'active' } : {}),
    }, computedLines),
    [prefill, lineItems, metadata, discounts, baseSummary, computedLines, overallMargin, taxRate, taxExempt, taxSource, marginUserEdited, loadedOverallMargin, quoteMeta.status]
  );

  // IDI-347 (reopened): settings hydration must ALSO pin the autosave baseline.
  //
  // `hydrated: !loading` (below) only covers the QUOTE load. But
  // `loadAccessoryCatalog` runs on its own effect, independent of `loading`,
  // and sets pricing inputs -- categoryMargins, laborBurdenPct, laborDefaults,
  // customerTypes. Those feed computeQuote, so when they land AFTER `loading`
  // has already flipped false, the recomputed totals look exactly like a user
  // edit and the autosave fires an unprompted PUT.
  //
  // That is the second of the two writes in this ticket, and the half the
  // PR #436 review asked for but that was never implemented: "gate on BOTH
  // the quote load AND the settings load." Flipped in loadAccessoryCatalog's
  // finally so it becomes true on success OR failure -- a settings fetch that
  // 500s must not wedge autosave off forever.
  const [settingsHydrated, setSettingsHydrated] = useState(false);
  // IDI-534: true once the loaded lines' categoryMargin has been re-derived
  // from Settings (see the effect next to the catalog seeding).
  const [marginsSettled, setMarginsSettled] = useState(false);

  const { isDirty, onLeave, cancelPending, lockedError, error: autoSaveError } = useQuoteAutoSave(quoteId, quotePayload, {
    onSaved: async ({ id: savedId, status: savedStatus, quoteNumber: savedQuoteNumber }) => {
      if (!quoteId && savedId) {
        setQuoteId(savedId);
        // IDI-345: fold the server-assigned quote number into metadata so the
        // header renders "Quote Q-00NN" immediately. Without it,
        // formatQuoteNumber({ ...metadata, id: quoteId }) has no
        // metadata.quoteNumber for a net-new quote and falls back to the raw
        // UUID until reload (the LOAD effect only sets quoteNumber for existing
        // quotes). Only set when the create response returned one.
        if (savedQuoteNumber) {
          setMetadata(prev => ({ ...prev, quoteNumber: savedQuoteNumber }));
        }
        // IDI-332: mark this id as self-created BEFORE navigating, so the
        // load-and-reset effect's guard (below) sees it set on the very
        // first render after the URL change and skips its reset+refetch.
        justCreatedIdRef.current = savedId;
        // IDI-313: seed quoteMeta.status from the create response. A NET-NEW
        // quote's status is only ever set here — the LOAD effect that
        // populates quoteMeta (below) fires for EXISTING quotes only. Before
        // this, quoteMeta.status stayed null, so shouldActivateOnSave(null, n)
        // was ALWAYS false and the quote was stuck in `draft` forever (GPL-12,
        // could never convert to a job). Fall back to 'draft' since the
        // backend always creates net-new quotes as draft (cmd/quotes:1130).
        setQuoteMeta(prev => ({ ...prev, status: savedStatus ?? 'draft' }));
        // { replace: true } is required — without it, Back from here
        // returns to /quotes/add and starts ANOTHER fresh quote, which is
        // the same orphan bug from the other direction.
        // IDI-564: justCreatedQuoteId tells ConvertedQuoteGuard this id was
        // just created by this page, so it renders straight through (no
        // loader, no fetch) and the builder stays mounted. The origin state is
        // kept; quoteBack was captured once at mount, so Back is unaffected.
        navigate(`/quotes/${savedId}/edit`, { replace: true, state: { ...(location.state || {}), justCreatedQuoteId: savedId } });
      }

      // IDI-313 (2026-09-10): sync local status after an activation save so
      // the payload memo's `quoteMeta.status === 'draft'` guard stops firing
      // once the quote is actually active server-side. Without this, every
      // subsequent autosave would keep re-sending `status: 'active'`
      // (harmless — the backend's active→active hop is a no-op — but this
      // keeps local state honest and avoids relying on that no-op).
      if (shouldActivateOnSave(quoteMeta.status, lineItems.length)) {
        setQuoteMeta(prev => ({ ...prev, status: 'active' }));
      }

      // IDI-425 (ruled by Adrian 2026-10-09): the quote NEVER writes the
      // customer record. A customer is edited only in the customer drawer
      // (CustomerBlock -> CustomerFormPanel), whose Save writes it once, at
      // the time of the edit. The old DEF-120 writeback here re-PUT the whole
      // customer after EVERY quote save (measured 2026-10-15: one identical
      // PUT /v1/customers per quote PUT, starting with the first create),
      // so a quote open in one tab overwrote edits made to the customer
      // elsewhere. Its new-customer branch was unreachable: nothing sets
      // `prefill.isNewCustomer` (in-quote creates go through the drawer,
      // which POSTs the customer itself).
    },
    toast,
    enabled: (lineItems.length > 0 || !!prefill.customerId) && !isTemplateEdit,

    // IDI-347: autosave must not treat the async LOAD of an existing quote as
    // user edits. `loading` is true from mount until the loader's finally
    // (setLoading(false)) fires, so hydrated=!loading pins the dirty baseline
    // through hydration. Net-new quotes mount with loading=false → hydrated
    // true immediately (they save on the first real edit as before).
    hydrated: !loading && settingsHydrated && marginsSettled,
    // PT-53A W3.1 (DEF-119): strip price-bearing fields when the quote is
    // acknowledged so customer-info-only edits can save without triggering
    // the PT-42 409 lock.
    stripPriceFields: isQuoteLocked,
  });

  // PT-43.1 (2026-07-28): the autosave surfaces PT-42's document_locked 409
  // as `lockedError`. A banner rendered below the PageHeader (see JSX below)
  // shows both the reason ("why") and the CO exit affordance ("what do I do
  // instead?"), pairing the block with its exit per 43.3.

  // ─── Load Existing Quote ───────────────────────────────────────────────────

  // PT-14 A2 (Wave 1, 2026-07-24): SPA-navigating from /quotes/A/edit to
  // /quotes/B/edit was showing A's customer + line items in B's builder.
  // Root cause: (1) this effect didn't reset state before the fetch, so A's
  // state leaked into B's render; (2) `quoteId` state was seeded via
  // useState(id || null) exactly once and never re-synced to the URL, so
  // saves persisted to A's row even while viewing B; (3) any pending
  // autosave debounce fired with mismatched refs. Fix: reset state at the
  // top of the effect, cancel pending saves, and sync quoteId to URL id.
  useEffect(() => {
    if (!isEditing) return;
    // IDI-332 (2026-09-14): skip the reset+refetch when THIS id transition
    // is our own just-created quote gaining its id via navigate() in
    // onSaved above — not a genuine switch to a different quote. Checked
    // by provenance (a ref set at creation time), NOT by `id === quoteId`
    // equality: on a real hard reload, quoteId is ALSO freshly seeded from
    // the URL id (`useState(id || null)`), so equality would be true then
    // too — and skipping the fetch on a genuine reload is exactly the bug
    // this ticket exists to fix (the record would never load at all).
    // The ref only survives within one mounted session, so it's `null` on
    // every fresh mount (including reload) and this guard is a no-op then.
    if (justCreatedIdRef.current === id) {
      justCreatedIdRef.current = null;
      return;
    }
    // Cancel any pending autosave from a previously-loaded quote so a
    // debounced save from A can't clobber B's row after the id changes.
    if (typeof cancelPending === 'function') cancelPending();
    taxSiteRef.current = undefined; // IDI-668: re-seed the site for this quote
    // Reset all quote-scoped state up-front so the user doesn't see the
    // previous quote's data during the in-flight fetch.
    setPrefill(emptyPrefill());
    setLineItems([]);
    setDiscounts([]);
    setQuoteDocuments([]);
    setMetadata({ title: '', notes: '', validityDays: 30 });
    setInternalNotesList([]);
    setQuoteMeta({ status: null, updatedAt: null, updatedByName: null });
    // Sync quoteId state to the URL id so autosave writes to the right row.
    setQuoteId(id || null);
    setLoading(true);
    async function loadQuote() {
      try {
        const data = await quotesAPI.getQuote(id);
        if (data) {
          // PT-53A W3.1 (DEF-119): detect acknowledged lock on load.
          // IDI-132 (D-01): a sent, signed or won-without-a-job quote opens
          // here as the full quote, locked. Keys on signatureStatus too: a
          // signed quote may still read "sent" until IDI-370's backfill.
          setIsQuoteLocked(!!getQuoteLockState(data));

          // PT-15 #1 (2026-07-24): customer hydration — defensive path.
          // Root cause: the loader only read `data.customer_id` (snake_case)
          // and had no fallback to `data.customerId` or `data.customer?.id`.
          // When the backend returned the customer under any of the alternate
          // shapes, `prefill.customerId` was null → `CustomerBlock` rendered
          // its EMPTY (typeahead) variant instead of the CARD variant, and
          // autosave shipped `customer_id: null` back to the server — a
          // silent data-loss risk on reload.
          //
          // Fix (operator-approved 2026-07-24, Q2): if `data.customer` looks
          // like a full customer record (has contacts/addresses arrays or a
          // name), route through the tolerant `prefillFromCustomer()` path
          // that also re-projects the snapshot (payment terms + contacts +
          // addresses) from the fresh customer record. Otherwise fall back
          // to the current construction with a triple-alias on customerId.
          const custRecord = data.customer;
          const looksLikeCustomer = custRecord && typeof custRecord === 'object' && (
            Array.isArray(custRecord.contacts) ||
            Array.isArray(custRecord.addresses) ||
            typeof custRecord.name === 'string' ||
            typeof custRecord.first_name === 'string' ||
            typeof custRecord.firstName === 'string'
          );
          if (looksLikeCustomer) {
            const projected = prefillFromCustomer(custRecord);
            setPrefill({
              ...projected,
              // Preserve any explicit overrides that the quote itself carries
              // (payment term, contact selection, addresses) — the quote is
              // the point-in-time snapshot; the customer record is the
              // canonical source but the quote's stored values win.
              contact: data.contact || projected.contact,
              allContacts: data.contacts || projected.allContacts,
              // §4 (2026-07-29): read full objects first, fall back to IDs-only
              // for quotes saved before this fix (they only have salesperson_ids).
              salespersons: data.salespersons
                || (Array.isArray(data.salesperson_ids)
                  ? data.salesperson_ids.map(id => ({ id, name: '' }))
                  : projected.salespersons || []),
              paymentTerm: data.payment_term || projected.paymentTerm || 'net_30',
              paymentType: data.payment_type || projected.paymentType || '',
              billingAddress: data.billing_address || projected.billingAddress,
              serviceAddress: data.service_address || projected.serviceAddress,
              sameAsBilling: data.same_as_billing ?? projected.sameAsBilling ?? true,
              customerId: data.customer_id || data.customerId || custRecord.id || projected.customerId || null,
              customerName: data.customer_name || projected.customerName || '',
              isNewCustomer: false,
              leadSource: data.leadSourceTypeId || data.leadSource || data.lead_source || '',
              jobTypeId: data.jobTypeId || data.job_type_id || '',
              // IDI-169: jobTypeLabel is set server-side (getQuote) by
              // resolving jobTypeId's raw legacy id against the migrated
              // JOBTYPE# entities -- a different lookup than
              // settings.jobTypes[] (the tenant-configurable dropdown
              // list). Carried through so ProjectDetailsCard can show a
              // real name even when jobTypeId doesn't match any dropdown
              // option's value.
              jobTypeLabel: data.jobTypeLabel || '',
              measurementMethod: data.measurementMethod || data.measurement_method
                || (data.measure === 'true' ? 'field' : data.plans === 'true' ? 'blueprint' : ''),
              // IDI-178: legacy per-quote toggle. `??` (not `||`) so an
              // explicit stored `false` is respected rather than falling
              // through to the prior projected/default value.
              roundMaterials: data.roundMaterials ?? data.round_materials ?? projected.roundMaterials ?? false,
            });
          } else {
            // PT-53A W3.1 round 2 (DEF-123): the else branch fires when
            // `data.customer` is absent or minimal. If we have a customerId
            // but no embedded customer record, FETCH the customer so the
            // builder card renders the real name/contacts/address — not
            // "Untitled Customer". This is the edit-path sibling of DEF-101
            // (which fixed the create path).
            const resolvedCustId = data.customer_id || data.customerId || data.customer?.id || null;
            let custPrefill = {
              customerId: resolvedCustId,
              customerName: data.customer_name || '',
              contact: data.contact || null,
              allContacts: data.contacts || [],
              salespersons: data.salespersons || [],
              paymentTerm: data.payment_term || 'net_30',
              paymentType: data.payment_type || '',
              billingAddress: data.billing_address || emptyPrefill().billingAddress,
              serviceAddress: data.service_address || emptyPrefill().serviceAddress,
              sameAsBilling: data.same_as_billing ?? true,
              isNewCustomer: false,
              leadSource: data.leadSourceTypeId || data.leadSource || data.lead_source || '',
              measurementMethod: data.measurementMethod || data.measurement_method
                || (data.measure === 'true' ? 'field' : data.plans === 'true' ? 'blueprint' : ''),
              // IDI-178: legacy per-quote toggle.
              roundMaterials: data.roundMaterials ?? data.round_materials ?? false,
            };
            // If we have an ID, fetch the customer record to get contacts/addresses.
            if (resolvedCustId) {
              try {
                const fetched = await quotesAPI.getCustomer(resolvedCustId);
                if (fetched) {
                  const projected = prefillFromCustomer(fetched);
                  custPrefill = {
                    ...custPrefill,
                    ...projected,
                    customerId: resolvedCustId,
                    // IDI-169: quote-scoped fields survive the customer spread.
                    leadSource: custPrefill.leadSource,
                    jobTypeId: custPrefill.jobTypeId,
                    measurementMethod: custPrefill.measurementMethod,
                    // IDI-652 (D4): the stored quote term wins over the customer's.
                    paymentTerm: data.payment_term || projected.paymentTerm || 'net_30',
                  };
                }
              } catch (fetchErr) {
                // PT-53A W3.2 (DEF-125): surface the failure. A silent 404
                // renders "Untitled Customer" with no signal that anything
                // went wrong. The operator spent a save attempt discovering
                // what a one-line notice would have told them.
                // eslint-disable-next-line no-console
                console.warn(`[QuoteBuilder] Customer fetch failed for ${resolvedCustId}:`, fetchErr?.message || fetchErr);
                custPrefill.customerLoadError = `Customer record not found (${resolvedCustId}). The quote references a customer that may have been deleted.`;
              }
            }
            setPrefill(custPrefill);
          }
          setInternalNotesList(normalizeInternalNotes(data.internal_notes, data));
          setMetadata({
            title: data.title || '',
            notes: data.notes || '',
            validityDays: data.validity_days || 30,
            // PT-14 A1 (Wave 1, 2026-07-24): carry quote_number + sequence
            // through so the builder header renders the same Q-#### as the list.
            quoteNumber: data.quote_number || data.quoteNumber || null,
            sequence: data.sequence || null,
            // §4 (2026-07-29): re-hydrate T&C template selection so it persists across reload.
            tcTemplateId: data.tcTemplateId || data.tc_template_id || null,
            tcTemplateName: data.tcTemplateName || data.tc_template_name || '',
            // DEF-151 (2026-08-04): carry the loaded quote's subtotal and
            // snapshot so assembleQuotePayload can emit the snapshot-once
            // guard. The fresh subtotal is computed from lineItems at payload
            // assembly time; this field is only used for the snapshot source.
            _loadedSubtotal: data.subtotal ?? null,
            _loadedSubtotalSnapshot: data.subtotal_snapshot ?? data.subtotalSnapshot ?? null,
          });
          // PR-02b §1b.4: legacy read-tolerance guard. Old test quotes stored
          // accessories as SIBLINGS in line_items with categoryId==='accessories'.
          // Q4 said no migration — silently drop them so opening an old quote doesn't crash.
          // IDI-41: migrated quotes leave `data.line_items` empty and carry the
          // items inside `data.areas[].lineItems`. Fall back to flattening the
          // area-embedded items so the builder renders them (and their totals)
          // instead of "No line items yet" / $0.00.
          //
          // IDI-243 correction (2026-09-04): the backend now ALSO projects a
          // flat `line_items` from `areas[]` server-side (for surfaces with no
          // area-flattening fallback of their own, e.g. QuoteReviewSend's
          // reconciliation banner). That projection copies the raw migrated
          // shape verbatim (`materialCostCents`/`laborCostCents`/etc) -- it
          // does NOT do the cost-field-renaming (-> `materialCost`/`laborCost`)
          // or the IDI-47 cost-as-price stamp that `flattenAreaLineItems` does,
          // because those decisions are specific to the margin engine, not a
          // generic "give me flat line items" concern. If the builder used
          // `data.line_items` directly whenever it's non-empty, every migrated
          // quote's cost fields would read as $0 to the margin engine the
          // moment the backend fix ships -- confirmed live: quote 48655's flat
          // `line_items` carry `materialCostCents`/`laborCostCents` (raw), not
          // `materialCost`/`laborCost` (what costBreakdown/readEngineLine read),
          // silently blocking every margin edit ("Add item costs before
          // setting a margin").
          //
          // Fix (IDI-310, 2026-09-10): the precedence above was backwards
          // for one specific case, and a naïve "always prefer non-empty
          // line_items" swap (the first thing tried) would have reintroduced
          // the IDI-47 margin-engine bug this same block already fixed once.
          //
          // resolveLoadedLineItems (quote-builder/quotePrefill.js) is the
          // extracted, independently-tested version of this precedence rule
          // — see its docblock for the full two-producer/discriminator
          // rationale and quote-builder/resolveLoadedLineItems.test.js for
          // the falsifiers (a builder-saved line surviving reload is the
          // regression case; a never-edited migrated quote still flattening
          // via areas[] is the IDI-47 control).
          const rawLineItems = resolveLoadedLineItems(data);
          const filtered = rawLineItems.filter((li) => li && li.categoryId !== 'accessories');
          const dropped = rawLineItems.length - filtered.length;
          if (dropped > 0 && process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.warn(`[QuoteBuilder] Dropped ${dropped} legacy sibling accessory row(s) from line_items. This is expected for pre-PR-02b test quotes; no migration is written (Q4).`);
          }
          // Ensure every surviving line item has an accessories array (nested shape).
          // PT-31 (2026-07-27): also hydrate the backend's two-field margin
          // shape (`margin_pct` + `margin_override`) back into the frontend's
          // single nullable `lineMargin` field. Defensive read: if the loaded
          // record already has `lineMargin` set (older writes or in-flight
          // records mid-migration), respect it; otherwise reconstruct from
          // the two backend fields. `margin_override=false` → lineMargin=null
          // (inherited); `margin_override=true` → lineMargin=margin_pct
          // (including the explicit-zero case that PT-30 rules valid).
          const normalized = filtered.map((li) => {
            const hasFrontendMargin = li.lineMargin != null;
            const reconstructedMargin = li.margin_override === true
              ? Number(li.margin_pct) || 0
              : null;
            return {
              ...li,
              accessories: Array.isArray(li.accessories) ? li.accessories : [],
              lineMargin: hasFrontendMargin ? li.lineMargin : reconstructedMargin,
            };
          });
          // IDI-534: never trust a stored categoryMargin (pre-#596 lines carry
          // the old 35% / 50% seed). Re-derive from the dealer's Settings; the
          // effect below re-runs this if Settings land after the quote.
          setLineItems(rederiveCategoryMargins(normalized, categoryMarginsRef.current));
          // DEF-152 BP-2 (2026-08-04): on-load tax resolution cascade.
          // DEF-155 is fixed (59ce147) — safe to resolve on load now.
          // T1: per-quote override (stored tax_rate from a prior user action)
          // T2: location match (serviceAddress.state vs Settings tax rows)
          // T3: tenant default (Settings default row)
          // T4: none (0, per D-231 — no jurisdiction constant)
          // Sent/locked quotes use the frozen stamp; mutable quotes re-resolve.
          // IDI-380: restore a genuine exemption flag from the loaded quote.
          // Absent/false => "no rate set" (not exempt), so the Tax modal won't
          // falsely pre-check Tax Exempt on a quote that simply has no rate.
          setTaxExempt(!!(data.tax_exempt ?? data.taxExempt));
          // IDI-668: rebuild the saved source so opening writes nothing.
          setTaxSource((data.tax_source ?? data.taxSource) === 'override' ? 'override' : 'configured');
          // IDI-380: tiers live in resolveLoadedTaxRate (sent/locked -> stamp;
          // exempt -> 0, never re-resolved; stored rate; else Settings).
          const loadedTax = resolveLoadedTaxRate(data);
          if (!loadedTax.needsSettings) {
            setTaxRate(loadedTax.rate);
          } else {
            try {
              const taxRes = await settingsAPI.getTaxRates();
              const taxArr = Array.isArray(taxRes) ? taxRes : taxRes?.rates || taxRes?.data || [];
              setTaxRate(pickSettingsTaxRate(taxArr, data));
            } catch {
              // Settings unavailable — fall back to stored or 0.
              setTaxRate(data.tax_rate ?? 0);
            }
          }
          // PT-53A W3.2 (DEF-124): hydrate overallMargin from the persisted
          // `overall_margin` field. Without this, editing the margin in one
          // session and reloading reverts to the Settings default (0.35).
          // PR-18 P0: validate via normalizeMarginValue — reject 0 and >= 1.0
          // so a damaged record doesn't perpetuate the bad value.
          const loadedMargin = normalizeMarginValue(data.overall_margin ?? data.overallMargin ?? data.grossMargin);
          if (loadedMargin != null) {
            setOverallMargin(loadedMargin);
            setLoadedOverallMargin(loadedMargin); // u00a78: preserve on save without claiming user intent
          }

          // PR-C.B (2026-07-21): rehydrate `discounts` from the loaded quote.
          // Root cause of the "discount disappears on reload" ledger item: this
          // loader used to skip `data.discounts` entirely, so even though the
          // backend's generic PUT pass-through preserved the field, the frontend
          // reset `discounts` state to `[]` on every load. See plan
          // `context/plans/tech-debt-cleanup-post-pr4.md` §3 PR-C.B.
          setDiscounts(Array.isArray(data.discounts) ? data.discounts : []);

          // IDI-289 (2026-09-09): populate the Files tab from the canonical
          // `documents[]` key. Defensive merge with `attachments[]` (legacy)
          // matches the pattern at SentQuoteRightPanel.js:202-203.
          setQuoteDocuments(mergeDocumentEntries(data));

          // PR-18: cache the header meta bits so we can render "status" +
          // "last edited" without another fetch.
          setQuoteMeta({
            status: data.status || 'draft',
            updatedAt: data.updated_at || data.updatedAt || null,
            updatedByName: data.updated_by_name || data.updatedByName || null,
            // PT-43.1 (2026-07-28): needed by the document_locked banner to
            // route the CO exit to /jobs/:jobId/change-orders/new. Accept
            // both camelCase (frontend canonical) and snake_case (raw DDB).
            convertedJobId: data.convertedJobId || data.converted_job_id || null,
            // IDI-364: what decides whether an open quote may re-price from
            // the Item Catalog (sent / pending / signed / converted never do).
            signatureStatus: data.signatureStatus || data.signature_status || null,
            signatureSentAt: data.signatureSentAt || data.signature_sent_at || null,
          });

          // PR-21: hydrate the stored margin pair for the migrated-quote adapter.
          const rawGM = data.grossMargin ?? data.gross_margin;
          const rawBid = data.bidAmountCents ?? data.bid_amount_cents ?? data.bidAmount;
          if (rawGM != null && rawBid != null) {
            const gmNum = Number(rawGM);
            // Normalize: percent-scale string to fraction. Do NOT heuristic < 1.
            const gmFraction = gmNum / 100; // grossMargin is ALWAYS percent-scale (287_ §2.1)
            setStoredMarginPair({
              grossMargin: gmFraction,
              bidAmountCents: Number(rawBid) || 0,
            });
          }

          // PR-C.B (2026-07-21): future-safe rehydration for `quote_options`.
          // Options currently live in line_items with areaLabel:'Options' (per
          // PR-A). If a quote is stored with the shape decided by Q3=B — dedicated
          // `quote_options[]` field — splice those entries onto line_items so the
          // builder renders them under the Options area regardless of storage
          // shape. Redundant today; safe for the future backend PR.
          if (Array.isArray(data.quote_options) && data.quote_options.length > 0) {
            const optionsAsLineItems = data.quote_options.map((opt) => ({
              id: opt.id || `opt-${Math.random().toString(36).slice(2, 10)}`,
              areaId: 'options',
              areaLabel: 'Options',
              name: opt.name || 'Option',
              description: opt.description || '',
              total: Number(opt.total) || 0,
              qty: 1,
              accessories: [],
              // Mark provenance so a future migration can round-trip cleanly.
              _sourceShape: 'quote_options',
            }));
            setLineItems((prev) => [...prev, ...optionsAsLineItems]);
          }
        }
      } catch {
        toast.error('Failed to load quote');
      } finally {
        setLoading(false);
      }
    }
    loadQuote();
  }, [id, isEditing, toast, cancelPending]);

  // ─── Load Existing Template (F.3) ────────────────────────────────────

  useEffect(() => {
    if (!isTemplateEdit) return;
    let cancelled = false;
    async function loadTemplate() {
      try {
        const list = await settingsAPI.getQuoteTemplates();
        const match = (list || []).find((t) => String(t.id) === String(editingTemplateId));
        if (!match) {
          toast.error('Template not found');
          navigate('/settings/quote-templates');
          return;
        }
        if (cancelled) return;
        setEditingTemplate(match);
        setLineItems(rederiveCategoryMargins(match.line_items || [], categoryMarginsRef.current));
        if (match.metadata) {
          setMetadata((prev) => ({ ...prev, ...match.metadata }));
        }
      } catch {
        if (!cancelled) toast.error('Failed to load template');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadTemplate();
    return () => { cancelled = true; };
  }, [isTemplateEdit, editingTemplateId, navigate, toast]);

  // Iter18 R2 F.3: Load available templates once, so the "Use Template" modal
  // has something to show. (Previously the templates state was populated only
  // by local "Save as Template" calls — the API list was ignored.)
  useEffect(() => {
    let cancelled = false;
    async function loadTemplates() {
      try {
        const list = await settingsAPI.getQuoteTemplates();
        if (!cancelled) setTemplates(list || []);
      } catch { /* silent; empty list = graceful degrade */ }
    }
    loadTemplates();
    return () => { cancelled = true; };
  }, []);

  // ─── Modal State ───────────────────────────────────────────────────────────

  const [cascadeOpen, setCascadeOpen] = useState(false);
  const [cascadeDefaultArea, setCascadeDefaultArea] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [customModalOpen, setCustomModalOpen] = useState(false);
  // PR-62 (2026-08-19): editingOption state + the OptionForm-in-Dialog
  // that consumed it were removed. Per 361_ §2 ("nothing about a group is
  // authored, so there is no Edit surface"), an option group has no edit
  // affordance — only Remove, preserved atomically via handleRemoveOption
  // (see 363_ §2).
  const [alternateParent, setAlternateParent] = useState(null);
  const [altPickerAreaId, setAltPickerAreaId] = useState(null); // DEF-032: area-header entry needs a line picker first
  const [taxModalOpen, setTaxModalOpen] = useState(false);
  const [noteModalItem, setNoteModalItem] = useState(null);


  // IDI-365: false until the tenant catalog has loaded (or failed). While
  // false, buildLineItem can't resolve a tenant product, so the edit sheet
  // holds "Add to quote" instead of committing an edit that would be lost.

  // IDI-364: lines re-priced from the Item Catalog on open (shown in a notice).
  const [repricedLines, setRepricedLines] = useState([]);
  const repricedForQuoteRef = useRef(null);

  // IDI-364 (RULED Lindsey 09-15, Adrian 09-15 + 09-29): an OPEN quote picks
  // up Item Catalog changes to its lines' cost and coverage when it is
  // opened. Each line keeps its product, sq ft, chosen coverage and own
  // margin override. Sent, signed or converted quotes never re-price
  // (isQuoteRepriceable). Runs ONCE per opened quote, after BOTH the quote's
  // lines and the catalog have loaded, and only rewrites lines that actually
  // moved, so the autosave writes exactly the corrected prices.
  useEffect(() => {
    if (loading || !quoteId || repricedForQuoteRef.current === quoteId) return;
    if (!Array.isArray(insulationCatalog) || insulationCatalog.length === 0) return;
    repricedForQuoteRef.current = quoteId;
    setLineItems((prev) => {
      const { lines, changed } = repriceOpenQuoteLines(quoteMeta, prev, insulationCatalog);
      if (changed.length) setRepricedLines(changed);
      return changed.length ? lines : prev;
    });
  }, [loading, quoteId, insulationCatalog, quoteMeta]);

  // PR-62 (2026-08-19, retires PT-16 optionMarginDefault hydration):
  //   The OptionForm dialog is gone (§361_ §1 Q2) — there is no "Own margin %"
  //   field to seed. Per-line margin for option lines is captured in the
  //   item form directly (§3ak) and stamped as `lineMargin` on the line
  //   (see AddItemCascade commitGroups / submitCustomItem). The
  //   `categoryMargins` state below is retained because AddItemCascade
  //   uses it for the composite line-margin lookup (unrelated to Options).
  // IDI-457: `categoryMargins` now comes from useBuilderCatalog. Retained as
  // a value (not state) because AddItemCascade uses it for the composite
  // line-margin lookup.


  // IDI-457: `tenantLaborDefaults` (B5 labor_rates) now comes from useBuilderCatalog.

  // PR-02b: load accessory catalog from settings on mount and after custom-accessory creates.
  // IDI-457: the catalog/pricing half of the old `loadAccessoryCatalog` moved
  // into useBuilderCatalog (shared with the change-order builder). What
  // remains here is the seeding that is QUOTE-ONLY, driven off the same
  // `catalogSettings` the hook already fetched — no extra round-trip.
  // IDI-534 (REQ-002): re-derive every loaded line's categoryMargin from the
  // dealer's Settings once they arrive (the catalog loads asynchronously and
  // can land after the quote). This runs in the same commit catalogLoaded
  // flips, before settingsHydrated, so the dirty baseline absorbs it and
  // opening a quote writes nothing. rederiveCategoryMargins returns the same
  // array on a no-op, so there is no extra render.
  useEffect(() => {
    if (!catalogLoaded) return;
    setLineItems((prev) => rederiveCategoryMargins(prev, categoryMargins));
  }, [catalogLoaded, categoryMargins]);
  // The autosave baseline may only stop re-seeding once the line state
  // ALREADY carries the re-derived margins. Flipping it in the same render
  // as the re-derive would read the re-derive as a user edit and save the
  // quote on open. This effect only sets it when re-deriving is a no-op.
  useEffect(() => {
    if (marginsSettled || !catalogLoaded || loading) return;
    if (rederiveCategoryMargins(lineItems, categoryMargins) === lineItems) setMarginsSettled(true);
  }, [marginsSettled, catalogLoaded, loading, lineItems, categoryMargins]);

  useEffect(() => {
    if (!catalogLoaded) return;
    const settings = catalogSettings;
    const md = settings?.marginDefaults;

    // §2 (2026-08-07): hydrate the master default margin from Settings.
    //
    // IDI-47 (2026-09-04, live-verified): only apply it to a NEW quote. On an
    // edit page the quote's own margin loads from a separate, slower fetch;
    // Settings is cached and fast, so seeding here would overwrite the
    // quote's real margin. 61 quotes were contaminated live by that race.
    // This is also why the seed CANNOT move into the shared hook.
    const stored = md?.master_default_margin ?? settings?.master_default_margin; // IDI-397 D2
    if (stored != null && Number.isFinite(Number(stored))) {
      const normalised = normalizeMarginValue(Number(stored));
      if (normalised != null && !isEditing && !isTemplateEdit) setOverallMargin(normalised);
    }

    let cancelled = false;
    (async () => {
      try {
        // The remaining seeding applies to a brand-new quote only. An edit
        // page must not be re-seeded (IDI-47), but it still has to reach the
        // `finally` below so the autosave baseline gets pinned.
        if (isEditing || isTemplateEdit) return;

        // IDI-378: Settings stores quote defaults under `quoteDefaults`
        // (camelCase; snake_case `quote_defaults` is ABSENT on tenant 1).
        const defaults = settings?.quoteDefaults || settings?.quote_defaults || settings || {};

        // DEF-152 T3 / IDI-668: the tax rate is resolved from Tax & Rates by
        // the site-state effect below, so a ?tab= re-run never resets it.

        // PR-PT-01: seed payment defaults.
        const defaultPaymentType = defaults.payment_type || defaults.paymentType || '';
        // IDI-652: Settings saves `payment_terms` (plural).
        const defaultPaymentTerm = defaults.payment_terms || defaults.payment_term || defaults.paymentTerm || 'net_30';
        if ((defaultPaymentType || defaultPaymentTerm) && !cancelled) {
          setPrefill((prev) => ({
            ...prev,
            ...(defaultPaymentType && !prev.paymentType ? { paymentType: defaultPaymentType } : {}),
            // IDI-652 (D1): never replace a customer's own term (Net 30 included).
            ...(defaultPaymentTerm && !prev.customerId ? { paymentTerm: defaultPaymentTerm } : {}),
          }));
        }

        // PT-53A W2.2 (DEF-101 + DEF-016): seed the customer from the query
        // param, so arriving from CustomerDetail doesn't make the user
        // re-type a customer they just came from.
        const seedCustomerId = searchParams.get('customerId');
        if (seedCustomerId) {
          try {
            const cust = await quotesAPI.getCustomer(seedCustomerId);
            if (cust && !cancelled) {
              const projected = prefillFromCustomer(cust, { defaultPaymentTerm });
              setPrefill((prev) => ({ ...prev, ...projected, customerId: seedCustomerId }));
            }
          } catch { /* non-fatal — the user can still pick manually */ }
        }
      } finally {
        // IDI-347: must be in `finally`. A settings fetch that 500s would
        // otherwise wedge autosave off for the whole session.
        if (!cancelled) setSettingsHydrated(true);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogLoaded, catalogSettings, isEditing, isTemplateEdit, searchParams]);

  // ─── IDI-668: tax follows the job site's state ──────────────────────────────
  // The site is the Job address the payload writes as service_address.
  const siteState = String((prefill.sameAsBilling ? prefill.billingAddress?.state : prefill.serviceAddress?.state) || '')
    .trim().toUpperCase();
  useEffect(() => {
    if (isTemplateEdit) return undefined;
    let cancelled = false;
    (async () => {
      let arr = [];
      try {
        const res = await settingsAPI.getTaxRates();
        arr = Array.isArray(res) ? res : res?.rates || res?.data || [];
      } catch { /* Settings unavailable — stays at 0 per D-231 */ }
      if (!cancelled) setTaxRows(Array.isArray(arr) ? arr : []);
    })();
    return () => { cancelled = true; };
  }, [isTemplateEdit]);
  // D1/D2: an automatic rate is the site state's row, else the default, else
  // the first, and re-resolves when the site's state changes. D4: opening a
  // quote only records its site; the loaded rate stays until the site moves.
  useEffect(() => {
    if (!taxRows || loading || isTemplateEdit) return;
    const first = taxSiteRef.current === undefined;
    if (!first && taxSiteRef.current === siteState) return;
    taxSiteRef.current = siteState;
    if ((first && isEditing) || taxSource === 'override' || taxExempt || isQuoteLocked) return;
    setTaxRate(pickSettingsTaxRate(taxRows, { service_address: { state: siteState } }));
  }, [taxRows, loading, siteState, isEditing, isTemplateEdit, taxSource, taxExempt, isQuoteLocked]);
  const taxNote = taxExempt || !taxRows ? null : describeTaxRate(taxRows, taxRate, siteState);
  const panelSummary = useMemo(
    () => (taxNote && effectiveSummary ? { ...effectiveSummary, taxNote } : effectiveSummary),
    [effectiveSummary, taxNote],
  );

  // D6 (V697): a customer picked before Settings arrived got Net 30 as the
  // fallback term. Once Settings arrive, give a customer with no terms of
  // its own the dealer's term, exactly as a later pick would.
  const handleHeaderPrefillChange = useCallback((next) => {
    if (typeof next !== 'function') {
      earlyPickRef.current = next?.customerId && !catalogLoaded ? next.customerId : null;
    }
    setPrefill(next);
  }, [catalogLoaded]);
  useEffect(() => {
    const custId = earlyPickRef.current;
    if (!catalogLoaded || !custId) return;
    earlyPickRef.current = null;
    if (!dealerDefaultPaymentTerm || prefill.customerId !== custId || prefill.paymentTerm !== 'net_30') return;
    // The updater re-checks, so a later pick or edit is never overwritten.
    quotesAPI.getCustomer(custId).then((cust) => {
      if (!cust) return;
      const term = prefillFromCustomer(cust, { defaultPaymentTerm: dealerDefaultPaymentTerm }).paymentTerm;
      setPrefill((prev) => (prev.customerId === custId && prev.paymentTerm === 'net_30' ? { ...prev, paymentTerm: term } : prev));
    }).catch(() => { /* keeps Net 30 */ });
  }, [catalogLoaded, dealerDefaultPaymentTerm, prefill.customerId, prefill.paymentTerm]);



  // PR-02b §1b.3: create a custom accessory inline from the picker.
  // Scopes the new accessory to the current line's area (Q3 landing).
  // Returns the saved catalog row so the picker can immediately add it to the line.
  const handleCreateCustomAccessory = useCallback(async (draft) => {
    // draft: { name, unit, unit_cost, areaId } (unit_cost in cents; CustomAccessoryForm already converted)
    // IDI-724 (D1, D2): a dealer Item Catalog row — own sku (buildCatalogRows
    // keeps sku'd overlay rows), Accessories, Active — scoped to the picker's
    // area (Add Item has no editingItem, so the picker passes it).
    const areaId = draft?.areaId || editingItem?.areaId;
    const id = `item-${Date.now()}`;
    const newRow = {
      id,
      sku: id,
      name: draft.name,
      itemType: 'accessory',
      categoryId: 'accessories',
      status: 'active',
      unit: draft.unit,
      itemCostPerUnit: draft.unit_cost,
      areaScope: areaId ? [areaId] : [],
      // Reasonable defaults for other QuoteItemsPage fields (unused for accessories):
      salesDescription: '',
      partNumber: '',
      squareFeetPerUnit: '',
      defaultVendor: '',
      defaultWarehouse: '',
      isCustom: true,
    };
    // IDI-224: narrow-write. PUT /v1/settings does partial-key merge on the
    // BACKEND: the server does its own fresh read at write time and only
    // overwrites the top-level keys present in the request body (see
    // backend-go/cmd/settings/main.go:1479-1545). So sending only the key
    // this handler owns is not just correct — it's what the endpoint expects.
    //
    // What was wrong before: `updateSettings({ ...current, quote_items: nextItems })`.
    // The spread converts the server's safe partial-key merge into a stale
    // client-side full overwrite — this client sends every top-level key
    // as of ITS earlier getSettings() read, and every one of those overwrites
    // the server's fresher value. That is the mechanism (not just a
    // consistent story) for Lindsey's UAT symptom "settings get messy" —
    // any settings change made in another window between the read and the
    // write is silently clobbered on save.
    //
    // Why the bug looks like carefulness: there are two OPPOSITE contracts
    // in backend-go/cmd/settings/main.go. The internal-Go `saveSettings()`
    // path at :502 REPLACES — its comment reads "NEVER assemble a partial
    // map — silently deletes company_profile, every other list, all
    // templates." Reading that warning and applying it to a frontend call
    // writes exactly `{...current, quote_items}`. The internal-Go rule and
    // the HTTP handler rule are inverse; the frontend caller wants the
    // HTTP handler's contract.
    //
    // Portica IDI-224 (2026-08-31) traced this to source and confirmed
    // the semantics before this fix landed. The read (getSettings) is
    // still needed for the append; only the write shape narrows.
    try {
      const current = await settingsAPI.getSettings();
      const existing = Array.isArray(current?.quote_items) ? current.quote_items : [];
      const nextItems = [...existing, newRow];
      await settingsAPI.updateSettings({ quote_items: nextItems });
      appendAccessory(newRow); // IDI-457: shared hook owns the catalog state
      toast.success(`“${draft.name}” saved to Settings`);
      return newRow;
    } catch (err) {
      toast.error('Failed to save accessory. Try again.');
      return null;
    }
  }, [editingItem, toast, appendAccessory]);

  // ─── Line Item Actions ─────────────────────────────────────────────────────

  const handleAddItem = useCallback((item) => {
    // PR-02b §1b.2: every new line item starts with an empty accessories array so
    // downstream code can assume `Array.isArray(item.accessories)`.
    const withAccessories = { ...item, accessories: Array.isArray(item.accessories) ? item.accessories : [] };
    setLineItems((prev) => [...prev, withAccessories]);
  }, []);

  // PR-06 FIX (2026-07-22): rename "Remove" → "Delete" in committed-line menu +
  // show an undo toast so mis-clicks are recoverable. Alternates cascade
  // naturally because they live inside `parent.alternates[]`.
  const handleRemoveItem = useCallback((itemId) => {
    let removedItem = null;
    let removedIndex = -1;
    setLineItems((prev) => {
      removedIndex = prev.findIndex((li) => li.id === itemId);
      if (removedIndex === -1) return prev;
      removedItem = prev[removedIndex];
      return prev.filter((li) => li.id !== itemId);
    });
    if (removedItem) {
      const label = removedItem.productLabel || removedItem.title || 'Item';
      toast.success(`“${label}” deleted`, {
        action: {
          label: 'Undo',
          onClick: () => {
            setLineItems((prev) => {
              const next = [...prev];
              const idx = Math.min(removedIndex, next.length);
              next.splice(idx, 0, removedItem);
              return next;
            });
          },
        },
      });
    }
  }, [toast]);

  const handleEditItem = useCallback((itemId, updates) => {
    // IDI-365: drop the cached total so the edit re-prices (a stale stored
    // total read as a "price override": walk v6, GM 35% -> 3.9%).
    setLineItems((prev) => prev.map((li) => li.id === itemId ? applyLineEdit(li, updates) : li));
  }, []);

  const handleUpdateMargin = useCallback((index, margin) => {
    setLineItems((prev) => updateLineMargin(index, margin, prev));
  }, [updateLineMargin]);

  // IDI-107 Part 2 (2026-08-25 ruling): handleUpdatePrice / the per-line
  // price override write affordance was removed — "user should not be able
  // to override cost per-line item". See CommittedLineCard.js for the full
  // rationale (it contradicted the margin engine's own precedence rule).

  // PR-19 (2026-07-22): custom-item quantity setter. Operator ruling: null on
  // creation is fine; the UI surfaces "Set quantity" in CommittedLineCard when
  // needed. This handler persists the value on the line item.
  // IDI-310 (2026-09-29): keep quantity + sqft in step and scale the line's
  // costs; see quote-builder/rescaleLineQuantity.js. Setting only `quantity`
  // left sqft (what the card, pricing and WO materials read) unchanged.
  const handleUpdateQuantity = useCallback((index, quantity) => {
    setLineItems((prev) => prev.map((li, i) => (i === index ? rescaleLineQuantity(li, quantity) : li)));
  }, []);

  // PT-32 (2026-07-27) 32.2: reorder ONE line-item within its area. `indices`
  // is the group's mapping of local (in-area) positions to flat lineItems[]
  // positions; we splice at the flat positions so the emergent area grouping
  // downstream (via `areaGroups`) preserves cohesion. No schema change:
  // area order remains emergent from `lineItems[]` order (Path A per recon).
  const handleReorderItem = useCallback((indices, fromLocalIdx, toLocalIdx) => {
    if (!Array.isArray(indices)) return;
    if (fromLocalIdx === toLocalIdx) return;
    if (fromLocalIdx < 0 || fromLocalIdx >= indices.length) return;
    if (toLocalIdx < 0 || toLocalIdx >= indices.length) return;
    const fromFlat = indices[fromLocalIdx];
    const toFlat = indices[toLocalIdx];
    if (fromFlat == null || toFlat == null) return;
    setLineItems((prev) => {
      const next = prev.slice();
      const [moved] = next.splice(fromFlat, 1);
      // PT-53A (DEF-035): drop-on-target means "land where I dropped it"
      // in both directions. The prior `toFlat - 1` for forward moves caused
      // items to land one slot short of the drop target.
      const insertAt = toFlat;
      next.splice(insertAt, 0, moved);
      return next;
    });
  }, []);

  // PT-32 32.3: reorder a whole AREA. `areaGroups` is a memoised grouping of
  // lineItems[] by areaId. To move an area, we rebuild lineItems[] as the
  // concatenation of area runs in the new order.
  const handleReorderArea = useCallback((areaGroupsSnapshot, fromAreaIdx, toAreaIdx) => {
    if (!Array.isArray(areaGroupsSnapshot)) return;
    if (fromAreaIdx === toAreaIdx) return;
    if (fromAreaIdx < 0 || fromAreaIdx >= areaGroupsSnapshot.length) return;
    if (toAreaIdx < 0 || toAreaIdx >= areaGroupsSnapshot.length) return;
    const nextGroups = areaGroupsSnapshot.slice();
    const [moved] = nextGroups.splice(fromAreaIdx, 1);
    nextGroups.splice(toAreaIdx, 0, moved);
    // Flatten back to lineItems[] in the new area order. Each group.items
    // preserves the intra-area order, which is what 32.2 mutated.
    const flattened = nextGroups.flatMap((g) => g.items);
    setLineItems(flattened);
  }, []);

  // PR-62 (2026-08-19): Option handlers. `handleRemoveOption` drops every
  // line-item tagged with the given optionId with an Undo toast (mirrors
  // handleRemoveItem's pattern). `handleEditOption` + `applyOptionEdit` are
  // gone (§361_ §1 Q2 — "nothing about a group is authored"). Remove is
  // preserved per §363_ §2: deleting a group is not editing it and remains
  // the only way to atomically drop every line in one action.
  const handleRemoveOption = useCallback((optionGroup) => {
    const optionId = optionGroup?.optionId;
    if (!optionId || optionId === '__ungrouped__') return;
    let removed = [];
    setLineItems((prev) => {
      removed = prev.filter((li) => li.optionId === optionId);
      return prev.filter((li) => li.optionId !== optionId);
    });
    if (removed.length) {
      // PR-62 (§3au): the group has no authored name. The toast used to
      // read the retired `optionGroup.optionName`; now it says "Option
      // removed" without a number — numbers are derived (deriveOptionLabel)
      // and threading the ordered id list here just for a toast is not worth
      // the plumbing. The Undo affordance is what matters.
      toast.success('Option removed', {
        action: {
          label: 'Undo',
          onClick: () => setLineItems((prev) => [...prev, ...removed]),
        },
      });
    }
  }, [toast]);

  // IDI-417: the drawer stays mounted across an add (it is "add another"
  // capable), and `alternateParent` holds an OBJECT REFERENCE into lineItems.
  // Rebuilding the parent with `.map()` below replaces that object, so the
  // held reference goes stale and is no longer present in the array. Two
  // visible failures followed:
  //   1. AddAlternateModal's `[parentItem]` effect re-ran with the stale
  //      object; `parentItem?.productLabel` was undefined on the replacement
  //      path, rendering the literal string "For: undefined", and sqft reset
  //      to '' (losing the inherited square footage).
  //   2. QuoteBuilderPage computes
  //      `computedLines[lineItems.indexOf(alternateParent)]`. indexOf on a
  //      stale reference returns -1, so `computedLines[-1]` is undefined and
  //      the price-diff preview silently read 0.
  // Re-point alternateParent at the NEW object in the same update so the
  // drawer re-arms against live data.
  const handleAddAlternate = useCallback((parentId, alternate) => {
    setLineItems((prev) => prev.map((li) => {
      if (li.id !== parentId) return li;
      return { ...li, alternates: [...(li.alternates || []), alternate] };
    }));
    // Derive the replacement separately -- never assign from inside an updater,
    // React may invoke it twice under StrictMode.
    setAlternateParent((cur) => {
      if (!cur || cur.id !== parentId) return cur;
      return { ...cur, alternates: [...(cur.alternates || []), alternate] };
    });
  }, []);

  // ─── Area Grouping ─────────────────────────────────────────────────────────

  const areaGroups = useMemo(
    // §358 (2026-08-19, root cause found by operator + Portica live on quote
    // 2216): grouping goes through the shared `groupLinesByArea` helper
    // because a plain-Object build + `Object.values()` re-sorted integer-like
    // areaIds (from migrated quotes like 2216: "1381", "1382", ...) ASCENDING
    // NUMERICALLY on every recompute, silently discarding user reorders.
    // Net-new quotes use slug areaIds ('walls', 'options', ...) which stayed
    // in insertion order — that's why the regression hid behind PR-58's
    // routing fix. See ChangeOrderBuilderPage for the sibling site.
    () => groupLinesByArea(
      lineItems,
      (item, areaId) => ({ areaId, areaLabel: item.areaLabel || areaId, items: [], indices: [] }),
      (g, item, idx) => { g.items.push(item); g.indices.push(idx); },
    ),
    [lineItems],
  );

  // PT-32 (2026-07-27) 32.3: shared drag primitive at the AREA level. Each
  // <AreaCard> gets `getItemProps(idx)` so the whole card is draggable and
  // accepts drops from other areas. dragType='area' disambiguates from the
  // per-line 'line-item' drags inside each AreaCard — a stray line-item drop
  // on an area wrapper (or vice versa) is a silent no-op.
  const areaDragReorder = useDragReorder({
    itemCount: areaGroups.length,
    onReorder: (fromIdx, toIdx) => handleReorderArea(areaGroups, fromIdx, toIdx),
    dragType: 'area',
  });

  // ─── Navigation ────────────────────────────────────────────────────────────


  // IDI-133 completion (2026-09-01, Portica #40): Convert to Job handler.
  // Navigates to /quotes/:id/record-decision — same target as every other
  // Convert site (QuoteDetail primary CTA, QuotesPage row menu, bulk bar,
  // Review & Send header). No local status check; the More menu already
  // gates on actions.canConvertToJob (state predicate).
  const handleConvertToJob = useCallback(() => {
    navigate(`/quotes/${id}/record-decision`);
  }, [id, navigate]);

  // IDI-133 (2026-09-01): handleMarkSent removed. Mark Sent retired.
  // IDI-132 (D-01): Mark Unsent is replaced by Reopen and Edit — the only way
  // to undo a send or a signature. Server action: voids the signature, kills
  // the signing link, records "reopened"; the builder then opens editable.
  const lockState = isQuoteLocked ? (getQuoteLockState(quoteMeta) || 'signed') : null;
  const handleReopen = useCallback(async () => {
    const signed = lockState === 'signed';
    const ok = await confirm(reopenConfirmOptions(signed));
    if (!ok) return;
    try {
      await quotesAPI.reopen(id);
      toast.success('Quote reopened for editing');
      setIsQuoteLocked(false);
      // Already on /quotes/:id/edit — the same builder, now editable.
      setQuoteMeta(prev => ({ ...prev, status: 'active', signatureStatus: null }));
    } catch (err) {
      toast.error('Failed to reopen: ' + (err?.message || 'Unknown error'));
    }
  }, [confirm, id, lockState, toast]);

  // IDI-132: locked-quote More menu actions (D-01 / IDI-533).
  const [markLostOpen, setMarkLostOpen] = useState(false);
  const handleDownloadPDF = useCallback(async () => {
    try {
      // IDI-487 (D6): the latest send's ticked PDFs and images follow the
      // quote, else none.
      let files = [];
      try {
        const full = await quotesAPI.getQuote(id);
        files = lastSendAttachments(full?.data || full).filter(canIncludeInPdf);
      } catch { /* none */ }
      const res = await api.generateQuotePDF(id, null, files);
      const data = res?.data || res;
      if (data?.pdf_base64) {
        const blob = new Blob([Uint8Array.from(atob(data.pdf_base64), c => c.charCodeAt(0))], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename || `quote-${id}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      } else if (data?.url) {
        window.open(data.url, '_blank');
      } else {
        toast.error('PDF generated but could not be downloaded.');
      }
    } catch (err) {
      toast.error(pdfRefusalMessage(err) || 'Failed to generate PDF.');
    }
  }, [id, toast]);
  const handleResend = useCallback(() => navigate(`/quotes/${id}/review`), [id, navigate]);
  const handleArchive = useCallback(async () => {
    if (!(await confirm({ title: 'Archive quote?', message: 'Archive this quote? It will be hidden from the active list.', confirmLabel: 'Archive' }))) return;
    try {
      await api.archiveQuote(id);
      navigate('/quotes');
    } catch {
      toast.error('Failed to archive quote.');
    }
  }, [confirm, id, navigate, toast]);
  const handleSubmitMarkLost = useCallback(async ({ reason }) => {
    try {
      await api.markQuoteAsLost(id, reason);
      await api.archiveQuote(id);
      setMarkLostOpen(false);
      navigate(`/quotes/${id}`);
    } catch {
      toast.error('Failed to mark as lost. Please try again.');
    }
  }, [id, navigate, toast]);
  const convertedJobIdMeta = quoteMeta.convertedJobId || quoteMeta.converted_job_id || null;
  const handleViewJob = useCallback(() => {
    if (convertedJobIdMeta) navigate(`/jobs/${convertedJobIdMeta}`);
  }, [convertedJobIdMeta, navigate]);

  // IDI-305: append one attributed internal note. The server stamps author
  // and time and returns the full list; adopt it as-is (another tab's
  // concurrent add comes back too). Works on a signed quote (no 409).
  const handleAddInternalNote = useCallback(async (content) => {
    if (!quoteId || !content || !content.trim()) return;
    try {
      const list = await quotesAPI.addQuoteNote(quoteId, content);
      setInternalNotesList(normalizeInternalNotes(list, { id: quoteId }));
    } catch (err) {
      toast.error('Failed to add note');
      throw err; // keep the composer's text so nothing typed is lost
    }
  }, [quoteId, toast]);
  // IDI-552: the author edits or deletes their own note (server: author only,
  // admins included). Adopt the server's list, as add does.
  const handleEditInternalNote = useCallback(async (note, content) => {
    if (!quoteId || !note?.id) return;
    try {
      const list = await quotesAPI.editQuoteNote(quoteId, note.id, content);
      setInternalNotesList(normalizeInternalNotes(list, { id: quoteId }));
    } catch (err) {
      toast.error(noteActionErrorMessage(err, 'save'));
      throw err; // keep the inline editor open with the typed text
    }
  }, [quoteId, toast]);
  const handleDeleteInternalNote = useCallback(async (note) => {
    if (!quoteId || !note?.id) return;
    try {
      const list = await quotesAPI.deleteQuoteNote(quoteId, note.id);
      setInternalNotesList(normalizeInternalNotes(list, { id: quoteId }));
    } catch (err) {
      toast.error(noteActionErrorMessage(err, 'delete'));
      throw err;
    }
  }, [quoteId, toast]);
  const sortedInternalNotes = useMemo(
    () => sortNotesNewestFirst(internalNotesList),
    [internalNotesList],
  );

  const handleReviewSend = useCallback(async () => {
    // PT-53A W2.1 (DEF-102): route the decision through `computeReviewSendTarget`
    // so no code path can fall off the end silently. The prior implementation
    // dropped the click into the void when `onLeave()` resolved to null and
    // `quoteId`/`id` were both null (fresh quote whose save failed) — the
    // primary CTA reported nothing while doing nothing. `§silent success is
    // worse than visible failure`. See reviewSendGuard.test.js RP-RG4.
    //
    // Preserves the pre-fix behaviour for the two happy paths (existing
    // `id`; save-then-navigate on fresh quotes) and adds a visible toast
    // for the save-required error branch.
    if (lineItems.length === 0 || !prefill.customerId) return;
    let savedId = null;
    if (isDirty || (!quoteId && !id)) {
      savedId = await onLeave();
    }
    const decision = computeReviewSendTarget({
      quoteId, urlId: id, savedId,
      hasCustomer: !!prefill.customerId,
      hasLines: lineItems.length > 0,
    });
    switch (decision.action) {
      case 'navigate':
        navigate(decision.to);
        return;
      case 'error':
        toast.error(decision.message);
        return;
      case 'skip':
      default:
        return;
    }
  }, [lineItems.length, prefill.customerId, isDirty, onLeave, quoteId, id, navigate, toast]);

  // ─── Save Template (F.3) ──────────────────────────────────────────────

  const handleSaveTemplate = useCallback(async () => {
    if (!editingTemplate) return;
    try {
      await settingsAPI.updateQuoteTemplate(editingTemplate.id, {
        name: editingTemplate.name,
        description: editingTemplate.description || '',
        line_items: lineItems,
        metadata,
      });
      toast.success(`Template "${editingTemplate.name}" saved`);
      navigate('/settings/quote-templates');
    } catch {
      toast.error('Failed to save template');
    }
  }, [editingTemplate, lineItems, metadata, navigate, toast]);

  // The primary CTA in the estimate footer dispatches on mode: template-edit
  // saves back to the template, otherwise proceeds to Review & Send.
  // PR-13 (2026-07-22): rename Finalize → Review and send per QB-2 v1.2.
  const primaryLabel = isTemplateEdit ? 'Save Template' : 'Review and send';
  const handlePrimary = isTemplateEdit ? handleSaveTemplate : handleReviewSend;

  // ─── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="px-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-64 bg-gray-100 rounded"></div>
        </div>
      </div>
    );
  }

  // PR-62 (2026-08-19): the PT-16 optionMarginDefault derivation retired
  // with the OptionForm dialog — that was its only consumer. Per-line margin
  // for option lines is captured in the item form directly (§3ak). Category
  // defaults still apply for lines that do not set their own margin per
  // §3ao precedence chain.

  return (
    <div className="min-h-screen">
      {/* Page Header */}
      <div className="px-6 pt-6">
        <PageHeader
          title={
            // IDI-324 §1b: keyed off `quoteId` STATE, not `isEditing` (`!!id`
            // from the URL param). On /quotes/add, `id` never changes after
            // the first autosave — the URL stays /quotes/add for the whole
            // session (IDI-332 tracks the deeper routing fix, out of scope
            // here) — but `quoteId` state IS set synchronously on first save
            // (onSaved above). Gating on `isEditing` meant the header stayed
            // stuck on "New Quote" even after the quote had a real number.
            // Do NOT add a navigate() to move the URL — ruled unsafe.
            isTemplateEdit
              ? 'Edit Quote Template'
              : quoteId
                ? `Quote ${formatQuoteNumber({ ...metadata, id: quoteId })}`
                : 'New Quote'
          }
          // IDI-157: Back = origin page and tab; template edit unchanged.
          backTo={isTemplateEdit ? '/settings/quote-templates' : quoteBack.href}
          backState={isTemplateEdit ? undefined : quoteBack.state}
          backLabel={isTemplateEdit ? 'Back to Templates' : quoteBack.label}
          badge={
            quoteId && !isTemplateEdit && quoteMeta.status
              ? <StatusPill status={resolveDisplayStatus(quoteMeta.status, lineItems.length)} size="sm" title="Quote status" />
              : undefined
          }
          meta={
            quoteId && !isTemplateEdit
              ? formatHeaderMeta(quoteMeta)
              : (!quoteId && !isTemplateEdit ? 'Not sent' : undefined)
          }
          actions={
            <div className="flex items-center gap-2">
              {/* Iter18 R2 F.3: header chip for template full-edit mode. This
                  chip slot is the golden-template pattern (per plan §1.7 Q3)
                  — Change Orders and other quote-shaped editors reuse it. */}
              {isTemplateEdit && editingTemplate && (
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                  style={{
                    backgroundColor: 'var(--color-surface-sunken)',
                    color: 'var(--color-text-default)',
                    border: '1px solid var(--color-border)',
                  }}
                >
                  Editing Template: {editingTemplate.name}
                </span>
              )}
              {/* Iter-18: Cancel + Finalize moved to sticky Estimate footer. */}
              {!isTemplateEdit && (
                <Button variant="secondary" onClick={() => setTemplateModalOpen(true)}>
                  Use Template
                </Button>
              )}
              {!isTemplateEdit && (
                <DropdownMenu
                  ariaLabel="More actions"
                  trigger={
                    <Button variant="secondary">
                      More <ChevronDownIcon className="h-4 w-4 ml-1 inline" aria-hidden="true" />
                    </Button>
                  }
                  items={[
                    { label: 'Quote Analysis', icon: <ChartBarIcon className="h-4 w-4 flex-shrink-0" />, onClick: () => setQuoteAnalysisOpen(true) },
                    { label: 'Material List', icon: <ClipboardDocumentListIcon className="h-4 w-4 flex-shrink-0" />, onClick: () => setMaterialListOpen(true) },
                    ...((() => {
                      // IDI-132 (D-01 / IDI-533): the More menu follows the lock.
                      //   sent:      Reopen and Edit, Download PDF, Resend, Mark lost, Archive
                      //   signed:    Reopen and Edit, Download PDF, Archive (no Resend: IDI-549)
                      //   converted: View Job only
                      //   editable:  Convert to Job
                      // Convert to Job is the locked quote's primary (banner + rail).
                      const icon = (I) => <I className="h-4 w-4 flex-shrink-0" />;
                      if (lockState === 'converted') {
                        return [{ divider: true }, { label: 'View Job', icon: icon(BriefcaseIcon), onClick: handleViewJob }];
                      }
                      if (lockState === 'sent' || lockState === 'signed') {
                        const a = [{ divider: true }, { label: 'Reopen and Edit', icon: icon(ArrowUturnLeftIcon), onClick: handleReopen }];
                        a.push({ label: 'Download PDF', icon: icon(ArrowDownTrayIcon), onClick: handleDownloadPDF });
                        if (lockState === 'sent') {
                          a.push({ label: 'Resend', icon: icon(PaperAirplaneIcon), onClick: handleResend });
                          a.push({ label: 'Mark lost', icon: icon(XCircleIcon), onClick: () => setMarkLostOpen(true) });
                        }
                        a.push({ label: 'Archive', icon: icon(ArchiveBoxIcon), onClick: handleArchive });
                        return a;
                      }
                      const actions = getAvailableActions((quoteMeta.status || 'active').toLowerCase(), quoteMeta);
                      const a = [];
                      if (actions.canConvertToJob) a.push({ label: 'Convert to Job', icon: icon(BriefcaseIcon), onClick: handleConvertToJob });
                      return a.length ? [{ divider: true }, ...a] : [];
                    })()),
                  ]}
                />
              )}
            </div>
          }
        />
      </div>

      {/* PT-43.1 (2026-07-28): document_locked banner. When the autosave hits a
          PT-42 409, this quote's price has been frozen at acceptance and the
          amendment path is a Change Order attached to the resulting job. The
          banner pairs the pill ("why can't I edit this?") with the CO route
          ("what do I do instead?") — per 43.3, shipping the block without
          the exit is worse than shipping neither. */}
      {/* IDI-313 (2026-09-10): surface useQuoteAutoSave's `error` — previously
          never destructured, so a hard save failure (network error, 5xx,
          etc.) was invisible. The user kept typing believing their work was
          saved. */}
      {autoSaveError && (
        <div className="px-6 pt-4">
          <Alert variant="error">{autoSaveError}</Alert>
        </div>
      )}
      {repricedLines.length > 0 && (
        <div className="px-6 pt-4" data-testid="reprice-notice">
          <Alert variant="info" onClose={() => setRepricedLines([])}>
            {repricedLines.length === 1 ? '1 line was' : `${repricedLines.length} lines were`} updated from the Item Catalog:{' '}
            {repricedLines.map((c) => `${c.label} (material ${formatCurrency(c.from)} → ${formatCurrency(c.to)})`).join('; ')}.
            {' '}Margins are unchanged.
          </Alert>
        </div>
      )}
      {/* IDI-398 (ruled 2026-10-05 / D-01): a signed quote opens as the full
        * quote, read-only. Notes, tags and files stay editable. */}
      {isQuoteLocked && !lockedError && (
        <div className="px-6 pt-4" data-testid="idi398-signed-banner">
          <Alert variant="info">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className="font-medium" data-testid="idi132-lock-label">{quoteLockLabel(lockState)}</div>
                <div className="text-sm mt-1">
                  {lockState === 'converted'
                    ? 'This quote has been converted to a job. Changes go through a change order on the job.'
                    : lockState === 'sent'
                      ? 'This quote has been sent to the customer. Internal notes, tags and files can still be edited. Lines, prices, the customer and terms change only through Reopen and Edit, which stops the signing link.'
                      : 'The customer accepted this quote. Internal notes, tags and files can still be edited. Lines, prices, the customer and terms change only through Reopen and Edit, which voids the signature.'}
                </div>
              </div>
              <div className="flex-shrink-0 flex items-center gap-2">
                {lockState === 'converted' ? (
                  convertedJobIdMeta ? <Button onClick={handleViewJob}>View Job</Button> : null
                ) : (
                  <>
                    <Button variant="secondary" onClick={handleReopen} data-testid="idi132-reopen">Reopen and Edit</Button>
                    <Button onClick={handleConvertToJob} data-testid="idi132-convert">
                      <BriefcaseIcon className="h-4 w-4" />Convert to Job
                    </Button>
                  </>
                )}
              </div>
            </div>
          </Alert>
        </div>
      )}
      {lockedError && (
        <div className="px-6 pt-4">
          <Alert variant="warning">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className="font-medium">This quote is locked</div>
                <div className="text-sm mt-1">
                  {lockedError.reason || 'This quote has been accepted; its price is frozen.'}
                  {' '}To modify pricing, create a Change Order attached to the resulting job.
                </div>
              </div>
              <div className="flex-shrink-0">
                {quoteMeta.convertedJobId || quoteMeta.converted_job_id ? (
                  <Button
                    onClick={() =>
                      navigate(
                        `/jobs/${quoteMeta.convertedJobId || quoteMeta.converted_job_id}/change-orders/new?fromAcceptedQuote=1&sourceQuoteId=${quoteId}`
                      )
                    }
                    data-testid="pt43-create-change-order"
                  >
                    Create Change Order
                  </Button>
                ) : (
                  <Button
                    disabled
                    title="Convert this accepted quote to a job first, then raise a change order there."
                  >
                    Create Change Order
                  </Button>
                )}
              </div>
            </div>
          </Alert>
        </div>
      )}

      {/* Two-column layout: Main (left) + Estimate (right).
          PT-15 #3 (2026-07-24): PT-14 C4 gave the left column its own scroll
          container (lg:max-h-[calc(100vh-6rem)] + lg:overflow-y-auto) so the
          right rail could stay sticky. That worked for the rail but created
          a nested scrollbar on the left column — the whole page no longer
          scrolled as one, and users saw two independent scrollbars on
          desktop. Fix: revert to plain flow. EstimatePanel's own
          `sticky top-6` handles the anchoring inside the natural page
          scroll of <main>. */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 px-6">
        {/* Main content */}
        <div className="space-y-6">
          {/* IDI-209: Quote builder tabs */}
          <Tabs
            tabs={QUOTE_TABS}
            value={activeTab}
            onChange={handleTabChange}
          />

          {/* Tab 1: Project Details */}
          {activeTab === 'details' && (<div {...lockedSectionProps(isQuoteLocked)} data-testid="idi398-details-lock">
          {/* Quote Header */}
          {/* IDI-269: moved ABOVE ProjectDetailsCard per Adrian's ruling on the
              2026-09-08 soft-launch call — customer golden card first,
              project details second. QuoteHeader retains only the customer
              block + auto-expire text (see its docblock post-PR-3), so this
              is a pure position swap, not a content change. */}
          <QuoteHeader
            prefill={prefill}
            onPrefillChange={handleHeaderPrefillChange}
            dealerDefaultPaymentTerm={dealerDefaultPaymentTerm}
            metadata={metadata}
            onMetadataChange={setMetadata}
            salespersonOptions={salespersonOptions}
            validityDays={metadata.validityDays}
            createdAt={isEditing ? new Date().toISOString() : null}
          />

          {/* IDI-169: Project Details — Job Type, Measurement, Lead Source */}
          {/* IDI-240: Project name (previously a bare <Input> above this card) now lives
              inside ProjectDetailsCard as its first field. See ProjectDetailsCard.js docblock. */}
          <ProjectDetailsCard
            prefill={prefill}
            onPrefillChange={setPrefill}
            projectTitle={metadata.title}
            onProjectTitleChange={(e) => setMetadata(prev => ({ ...prev, title: e.target.value }))}
          />

          </div>)}

          {/* Tab 2: Quote Detail */}
          {activeTab === 'quote' && (<div {...lockedSectionProps(isQuoteLocked)} data-testid="idi398-quote-lock">
          {/* Line Items Section */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                Line Items
              </h2>
              {/* PR-13 (2026-07-22): single CTA — no chevron, no split, no dropdown.
                  Options are chosen inside the sheet (step 1), not a separate button. */}
              <Button size="sm" onClick={() => { setCascadeDefaultArea(null); setCascadeOpen(true); }}>
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Item or Option
              </Button>
            </div>

            {/* Area Cards */}
            {areaGroups.length > 0 ? (
              <div className="space-y-4">
                {areaGroups.map((group, areaIdx) => {
                  const isDragging = areaDragReorder.draggingIndex === areaIdx;
                  const isDropTarget = areaDragReorder.dropTargetIndex === areaIdx;
                  return (
                    <div
                      key={group.areaId}
                      {...areaDragReorder.getItemProps(areaIdx)}
                      style={{
                        opacity: isDragging ? 0.4 : 1,
                        outline: isDropTarget && !isDragging
                          ? '2px solid var(--color-action-primary)'
                          : 'none',
                        outlineOffset: '2px',
                        borderRadius: 'var(--radius-md)',
                        transition: 'opacity 120ms ease',
                      }}
                    >
                      <AreaCard
                        areaId={group.areaId}
                        areaLabel={group.areaLabel}
                        items={group.items}
                        computedItems={group.indices.map((i) => computedLines[i])}
                        overallMargin={effectiveOverallMargin}
                        onEditItem={(item) => setEditingItem(item)}
                        onRemoveItem={handleRemoveItem}
                        onUpdateMargin={(localIdx, margin) => handleUpdateMargin(group.indices[localIdx], margin)}
                        onUpdateQuantity={(localIdx, quantity) => handleUpdateQuantity(group.indices[localIdx], quantity)}
                        onAddItem={(areaId) => { setCascadeDefaultArea(areaId); setCascadeOpen(true); }}
                        onAddAlternate={(itemOrAreaId) => {
                          // DEF-032: area-header passes a string (areaId); line-level passes an object.
                          if (typeof itemOrAreaId === 'string') {
                            setAltPickerAreaId(itemOrAreaId);
                          } else {
                            setAlternateParent(itemOrAreaId);
                          }
                        }}
                        onRemoveOption={(optionGroup) => handleRemoveOption(optionGroup)}
                        onReorderItem={(fromLocalIdx, toLocalIdx) =>
                          handleReorderItem(group.indices, fromLocalIdx, toLocalIdx)
                        }
                        canMoveAreaUp={areaIdx > 0}
                        canMoveAreaDown={areaIdx < areaGroups.length - 1}
                        onMoveAreaUp={() => areaDragReorder.moveItem(areaIdx, areaIdx - 1)}
                        onMoveAreaDown={() => areaDragReorder.moveItem(areaIdx, areaIdx + 1)}
                      />
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Empty state (F.4 polish): mode-aware copy. Templates-first flow
                 lands here on open with zero items — the guidance shifts to
                 emphasise that the operator is authoring reusable content. */
              <button
                type="button"
                onClick={() => setCascadeOpen(true)}
                className="w-full rounded-md border-2 border-dashed p-8 text-center transition-colors hover:border-red-200"
                style={{ borderColor: 'var(--color-border)' }}
              >
                <PlusIcon className="h-8 w-8 mx-auto mb-2" style={{ color: 'var(--color-text-secondary)' }} />
                <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                  {isTemplateEdit ? 'Add your first template line item' : 'No line items yet'}
                </p>
                <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                  {isTemplateEdit
                    ? 'Templates capture reusable line items you can drop into new quotes.'
                    : 'Pick an area \u2014 Ceilings, Walls, Floors, Basement, Air Sealing, Vent, Removal or Options \u2014 then build the line items inside it. Accessories are added on each line as you go.'}
                </p>
              </button>
            )}
          </div>

          {/* PR-13 (2026-07-22): the separate "Add Option" affordance is
              retired — users pick Options as an area inside the Add-Item sheet
              (step 1). Options still render inside the normal AreaCard when
              committed with areaLabel='Options'. */}

          {/* IDI-275 / IDI-280 / IDI-254 (2026-09-13/14): Attachments,
              Terms & conditions, and Notes cards ALL removed from the Quote
              Detail tab now — all three durably persist on Review & Send.
              Attachments moved first (IDI-275/#419): durable via R&S's
              dedicated Attachments card + the builder's own Files tab
              (Tab 4, IDI-289), so removing it from here lost no data.
              T&C and Notes stayed blocked until R&S actually persisted
              their edits (IDI-280/IDI-254/#423, useQuoteAutoSave) —
              before that PR, railNotes/tcTemplateId on R&S were LOAD-only,
              so removing these cards first would have lost data. Both
              live-verified post-#423 (real edit → debounce → direct API
              check → hard reload → value survived) before removing them
              here, per the IDI-275/#398 lesson: verify the replacement
              persists, not just that the control exists. NoteTemplateMenu
              (the "insert saved template" affordance the Notes card
              offered) has no equivalent on Review & Send yet — flagged as
              a known gap in the replacement surface, not fixed here; R&S's
              own textarea can still be typed into directly. */}

          </div>)}

          {/* Tab 3: Notes — internal project notes (not customer-facing).
              IDI-305: the canonical attributed NotesPanel (same as job,
              customer and work-order details). Append-only list of
              { id, content, author, author_id, created_at, updated_at },
              newest first by updated_at || created_at; no template menu
              (D-05). IDI-552: the note's author (only) edits or deletes it. Written only through
              ?action=add_note — never autosave — so it works on a signed
              quote too. Never reaches the customer document/PDF/signing page. */}
          {activeTab === 'notes' && (<>
          <p className="text-xs mb-2" style={{ color: 'var(--color-text-secondary)' }}>
            Internal — not shown to the customer
            {!quoteId && !isTemplateEdit ? '. Notes can be added once the quote has been saved.' : ''}
          </p>
          <NotesPanel
            notes={sortedInternalNotes}
            onAdd={quoteId && !isTemplateEdit ? handleAddInternalNote : undefined}
            onEdit={quoteId && !isTemplateEdit ? handleEditInternalNote : undefined}
            onDelete={quoteId && !isTemplateEdit ? handleDeleteInternalNote : undefined}
            currentUserId={currentUserId}
          />
          </>)}

          {/* Tab 4: Files — IDI-289 (2026-09-09). The DELETE surface per
              Adrian's ruling: users can delete files here (and from the
              customer/job detail Files tabs). Review & Send has remove/uncheck
              only — no delete affordance. Reads the canonical `documents[]`
              key (cmd/quotes/attachments.go:496-521) with a defensive merge
              of legacy `attachments[]`. Rendered via `entryDisplay` per the
              2026-08-03 ruling (AttachModal.js:50-51). */}
          {activeTab === 'files' && (
            <Card>
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-default)' }}>Files</h3>
                  <Button variant="secondary" size="sm" onClick={() => setAttachOpen(true)}>
                    Attach
                  </Button>
                </div>
                {quoteDocuments.length === 0 ? (
                  <EmptyState
                    title="No files attached"
                    description="Attach files to this quote using the button above. Attached files will be available on Review & Send."
                  />
                ) : (
                  <div className="space-y-2">
                    {quoteDocuments.map((doc, idx) => {
                      const d = entryDisplay(doc);
                      const rowKey = d.key || idx;
                      const handleDeleteDoc = async () => {
                        const attId = attachmentIdOf(doc);
                        if (!attId) return;
                        if (!(await confirm({
                          title: 'Delete file?',
                          message: `Remove "${d.name}" from this quote? This cannot be undone.`,
                          confirmLabel: 'Delete',
                          tone: 'danger',
                        }))) return;
                        try {
                          await api.deleteAttachment({ attachmentId: attId });
                          toast.success(`Deleted "${d.name}"`);
                          setQuoteDocuments(prev => removeDocumentById(prev, attId));
                        } catch (err) {
                          toast.error('Failed to delete: ' + (err?.message || 'Unknown error'));
                        }
                      };
                      return (
                        <div key={rowKey} className="flex items-center justify-between gap-2 text-sm py-1.5 border-b last:border-0" style={{ borderColor: 'var(--color-border)' }}>
                          <div className="flex items-center gap-2 min-w-0">
                            {/* IDI-445: the name opens the shared preview modal. */}
                            <button
                              type="button"
                              onClick={() => setPreviewFileKey(rowKey)}
                              className="truncate text-left hover:underline"
                              style={{ color: 'var(--color-text-default)' }}
                            >
                              {d.name}
                            </button>
                            {d.bytes > 0 && (
                              <span className="text-xs flex-shrink-0" style={{ color: 'var(--color-text-muted)' }}>
                                {d.bytes < 1024 ? `${d.bytes} B` : d.bytes < 1048576 ? `${(d.bytes / 1024).toFixed(1)} KB` : `${(d.bytes / 1048576).toFixed(1)} MB`}
                              </span>
                            )}
                          </div>
                          {previewFileKey === rowKey && (
                            <FilePreviewModal
                              entry={doc}
                              onClose={() => setPreviewFileKey(null)}
                              onDelete={attachmentIdOf(doc)
                                ? () => { setPreviewFileKey(null); return handleDeleteDoc(); }
                                : undefined}
                            />
                          )}
                          <button
                            type="button"
                            onClick={handleDeleteDoc}
                            className="flex-shrink-0 p-1 rounded hover:bg-gray-100"
                            style={{ color: 'var(--color-text-muted)' }}
                            aria-label={`Delete ${d.name}`}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Tab 5: Acknowledgments — IDI-540, one shared AcknowledgmentsPanel. */}
          {activeTab === 'acknowledgements' && (
            <Card>
              <div className="p-4">
                {id ? (
                  <AcknowledgmentsPanel documentType="quote" documentId={id} />
                ) : (
                  <EmptyState
                    title="No acknowledgment events yet"
                    description="Save and send this quote to start its acknowledgment history."
                  />
                )}
              </div>
            </Card>
          )}

        </div>

        {/* Right rail: Estimate Panel (sticky, hosts Cancel + Finalize footer per Iter-18 wire) */}
        {/* PT-15 #2 (2026-07-24): rail uses base-only summary so Options are
            excluded from the bid total — same math/predicate as R&S. See
            baseSummary derivation above. */}
        <EstimatePanel
          summary={panelSummary}
          /* IDI-309 / IDI-361: GM% and Bid Subtotal re-price the eligible
              lines ONCE (distributeToTarget). No overall_margin write. */
          onApplyTarget={handleApplyTarget}
          targetEditDisabledReason={isQuoteLocked ? `${quoteLockLabel(lockState)}. Use Reopen and Edit to change prices.` : (targetEditability.editable ? null : targetEditability.message)}
          targetCostBasis={baseSummary ? Math.round((baseSummary.bidSubtotal || 0) * (1 - (baseSummary.blendedMargin || 0))) : undefined}
          onEditTax={isQuoteLocked ? undefined : () => setTaxModalOpen(true)}
          discounts={discounts}
          onAddDiscount={isQuoteLocked ? undefined : (d) => setDiscounts(prev => [...prev, d])}
          onUpdateDiscount={isQuoteLocked ? undefined : (idx, d) => setDiscounts(prev => prev.map((item, i) => i === idx ? d : item))}
          onRemoveDiscount={isQuoteLocked ? undefined : (idx) => setDiscounts(prev => prev.filter((_, i) => i !== idx))}
          onCancel={() => navigate(isTemplateEdit ? '/settings/quote-templates' : '/quotes')}
          // IDI-132 (D-01): on a locked quote the rail's primary is Convert to
          // Job (View Job once converted); Review and send is for editable quotes.
          onFinalize={lockState === 'converted' ? (convertedJobIdMeta ? handleViewJob : undefined) : lockState ? handleConvertToJob : handlePrimary}
          finalizeLabel={lockState === 'converted' ? 'View Job' : lockState ? 'Convert to Job' : primaryLabel}
          finalizeDisabled={lockState ? false : (lineItems.length === 0 || !prefill.customerId)}
          finalizeHint={'Needs a customer and at least one line item.'}
          onSaveAsTemplate={!isTemplateEdit ? () => setSaveTemplateOpen(true) : undefined}
          saveAsTemplateDisabled={lineItems.length === 0}
        />
      </div>

      {/* ─── Modals ─────────────────────────────────────────────────────────── */}

      {/* IDI-132: Mark lost from a sent quote's More menu (same modal as QuoteDetail). */}
      <MarkAsLostModal
        open={markLostOpen}
        onCancel={() => setMarkLostOpen(false)}
        onSubmit={handleSubmitMarkLost}
      />

      <AddItemCascade
        open={cascadeOpen}
        onClose={() => setCascadeOpen(false)}
        onAddItem={handleAddItem}
        defaultAreaId={cascadeDefaultArea}
        accessoryCatalog={accessoryCatalog}
        onCreateCustomAccessory={handleCreateCustomAccessory}
        marginContext={wizardMarginContext}
        tenantCategoryMargins={categoryMargins}
        tenantLaborDefaults={tenantLaborDefaults}
        /* PR-66 s2c (Q-11): tenant Quote Items (insulation subset) surface
           in the Category / Product droplists and thread into buildLineItem
           so tenant-authored SKUs can be priced. Sourced from
           settings.quote_items (itemType in {spray_foam, blown_insulation}). */
        tenantQuoteItems={insulationCatalog}
        catalogLoading={!catalogLoaded}
        /* PR-62 (2026-08-19): current line items feed the Group selector's
           existing-groups list. See AddItemCascade.renderOptionGroupControls. */
        lineItems={lineItems}
        /* IDI-178: quote-level Round Materials toggle, threaded through to
           buildLineFromDraft so newly-added lines respect it. See
           ProjectDetailsCard.js / quote-builder/roundMaterials.js. */
        roundMaterials={prefill.roundMaterials === true}
      />

      {/* IDI-176: EditItemModal retired — the flat side-panel was a
          separate implementation of line-item editing with editable Margin %
          and Line Total (both non-user-editable per operator §19). Its
          replacement is the AddItemCascade wizard reopened in edit-mode
          with `initialItem` prefilled at step 2. Same fields, no shadow. */}
      <AddItemCascade
        open={!!editingItem}
        onClose={() => setEditingItem(null)}
        mode="edit"
        initialItem={editingItem}
        onEditItem={(id, updates) => {
          handleEditItem(id, updates);
          setEditingItem(null);
        }}
        onAddItem={() => { /* unreachable in edit mode; kept satisfied */ }}
        accessoryCatalog={accessoryCatalog}
        onCreateCustomAccessory={handleCreateCustomAccessory}
        marginContext={wizardMarginContext}
        tenantCategoryMargins={categoryMargins}
        tenantLaborDefaults={tenantLaborDefaults}
        tenantQuoteItems={insulationCatalog}
        catalogLoading={!catalogLoaded}
        lineItems={lineItems}
        roundMaterials={prefill.roundMaterials === true}
      />

      <CustomItemModal
        open={customModalOpen}
        onClose={() => setCustomModalOpen(false)}
        onAdd={handleAddItem}
        defaultAreaId={null}
      />

      <AddAlternateModal
        open={!!alternateParent}
        onClose={() => setAlternateParent(null)}
        parentItem={alternateParent}
        parentComputed={alternateParent
          ? computedLines[lineItems.findIndex((li) => li.id === alternateParent.id)]
          : null}
        onAdd={handleAddAlternate}
        tenantCategoryMargins={categoryMargins}
        tenantQuoteItems={insulationCatalog}
      />

      {/* DEF-032: line-item picker for area-header "Add alternate" entry.
          When triggered from an area header (not a specific line), the user
          must choose which line to add an alternate for. */}
      <Dialog open={!!altPickerAreaId} onClose={() => setAltPickerAreaId(null)} size="sm">
        <DialogHeader onClose={() => setAltPickerAreaId(null)}>Select line item</DialogHeader>
        <DialogBody>
          <p className="text-sm mb-3" style={{ color: 'var(--color-text-secondary)' }}>
            Which line item should the alternate be for?
          </p>
          <div className="space-y-1">
            {lineItems
              .filter(li => li.areaId === altPickerAreaId || li.area_id === altPickerAreaId)
              .map(li => (
                <button
                  key={li.id}
                  type="button"
                  className="w-full text-left px-3 py-2 rounded text-sm hover:bg-gray-100 transition-colors"
                  onClick={() => {
                    setAlternateParent(li);
                    setAltPickerAreaId(null);
                  }}
                >
                  <span className="font-medium">{li.productLabel || li.product_label || li.name || 'Unnamed'}</span>
                  {li.subAreaLabel && <span className="ml-2 text-xs" style={{ color: 'var(--color-text-muted)' }}>{li.subAreaLabel}</span>}
                  {li.sqft && <span className="ml-2 text-xs" style={{ color: 'var(--color-text-muted)' }}>{li.sqft} sq ft</span>}
                </button>
              ))}
          </div>
        </DialogBody>
      </Dialog>

      <TaxModal
        open={taxModalOpen}
        onClose={() => setTaxModalOpen(false)}
        taxRate={taxRate}
        taxExempt={taxExempt}
        jurisdiction={prefill.serviceAddress?.state || prefill.billingAddress?.state}
        rows={taxRows || []}
        keepRate={taxSource === 'override'}
        onSave={({ taxRate: newRate, taxExempt: newExempt }) => {
          // IDI-668 (D3): a changed non-exempt rate is the user's own.
          const changed = Math.abs(newRate - taxRate) > 1e-9;
          if (!newExempt && changed) setTaxSource('override');
          if (changed) setTaxRate(newRate);
          setTaxExempt(!!newExempt);
        }}
      />

      <NoteModal
        open={!!noteModalItem}
        onClose={() => setNoteModalItem(null)}
        initialNote={noteModalItem?.notes}
        itemLabel={noteModalItem?.productLabel}
        onSave={(note) => {
          if (noteModalItem) handleEditItem(noteModalItem.id, { notes: note });
        }}
      />

      {/* §2 (2026-08-08): quote-scope attach modal (B2 enabled). */}
      <AttachModal
        open={attachOpen}
        onClose={() => setAttachOpen(false)}
        scope="quote"
        ownerId={quoteId}
        ownerLabel={metadata.title || `Quote ${quoteId?.slice(0, 8) || ''}`}
        companyId={prefill.companyId}
        onAttached={async () => {
          setAttachOpen(false);
          // IDI-289: refresh the Files tab after a new attachment lands.
          // The server writes the ref to documents[] atomically; re-fetch
          // gives us the updated list without a full page reload.
          try {
            const refreshed = await quotesAPI.getQuote(quoteId);
            if (refreshed) {
              setQuoteDocuments(mergeDocumentEntries(refreshed));
            }
          } catch { /* Files tab will refresh on next full load */ }
        }}
      />

      {/* PR-62 (2026-08-19): the OptionForm-in-Dialog is retired per 361_ §1 Q2
          ("nothing about a group is authored, so there is no Edit surface").
          Option groups expose Remove only — see AreaCard's ⋮ menu + 363_ §2. */}

      {/* PR-02b: legacy per-area AccessoriesModal removed. Accessories now attach
          to line items via AccessoryPicker inside AddItemCascade (edit mode). */}

      {/* Template Modals (LAT-01) */}
      <ChooseTemplateModal
        open={templateModalOpen}
        onClose={() => setTemplateModalOpen(false)}
        templates={templates}
        onSelect={async (template) => {
          // IDI-213: strip any per-line margin the template may carry (older
          // templates saved before the save-side strip). Margin must resolve
          // from the TARGET customer's cascade at apply time, never from the
          // template. Defense-in-depth alongside the save-side strip.
          const items = stripTemplateMargin(template.line_items || template.lineItems || []);
          // §1: confirm when the quote already has line items
          if (lineItems.length > 0) {
            const ok = await confirm({
              title: 'Replace the items on this quote?',
              message: `This quote has ${lineItems.length} line item${lineItems.length !== 1 ? 's' : ''}. Applying a template will replace them. You can undo this straight after.`,
              confirmLabel: 'Apply template',
            });
            if (!ok) return;
          }
          // Snapshot for undo
          const snapshot = { lineItems: [...lineItems], metadata: { ...metadata } };
          // Apply
          if (items.length) setLineItems(rederiveCategoryMargins(items, categoryMargins));
          if (template.metadata) {
            const { title, quoteNumber, sequence, _loadedSubtotal, _loadedSubtotalSnapshot, ...safe } = template.metadata;
            setMetadata(prev => ({ ...prev, ...safe }));
          }
          toast.success(`Applied template "${toDisplayString(template.name, 'Untitled')}"`, {
            action: {
              label: 'Undo',
              onClick: () => {
                setLineItems(snapshot.lineItems);
                setMetadata(snapshot.metadata);
                toast.success('Template application undone');
              },
            },
            duration: 6000,
          });
        }}
      />
      <SaveTemplateModal
        open={saveTemplateOpen}
        onClose={() => setSaveTemplateOpen(false)}
        onSave={async ({ name, description }) => {
          // Iter18 R2 F.3: real API POST (was local-state stub). The templates
          // list is fetched fresh next time /settings/quote-templates mounts.
          // PR-11: destructure the single-object arg the modal passes
          // (SaveTemplateModal calls onSave({ name, description })). Prior
          // positional-args signature caused `name` to bind to the whole object,
          // which persisted as a nested-object row in DynamoDB and later crashed
          // /settings/quote-templates with React error #31.
          try {
            // DEC-62 (2026-08-04): strip quote-specific fields from template
            // metadata. A template must not carry project name, quote identity,
            // or DEF-151 internal fields into future quotes.
            const { title: _t, quoteNumber: _qn, sequence: _seq, _loadedSubtotal: _ls, _loadedSubtotalSnapshot: _lss, ...templateMetadata } = metadata;
            // IDI-213: a template must NOT carry margin. Margin resolves at
            // APPLY time from the target customer's cascade (category ->
            // customer -> customer-type). Line items carry a per-line
            // `lineMargin` (plus legacy margin_pct/margin_override/grossMargin);
            // if saved, a stale margin would override the new customer's
            // resolution when the template is applied. Strip them so applied
            // lines re-resolve fresh. (Customer/address/jobType/leadSource live
            // in `prefill`, not metadata, so they're already excluded; overall
            // margin is separate state and not saved either.)
            const templateLineItems = stripTemplateMargin(lineItems);
            await settingsAPI.createQuoteTemplate({
              name,
              description,
              line_items: templateLineItems,
              metadata: templateMetadata,
            });
            toast.success(`Template "${name}" saved`);
            setSaveTemplateOpen(false);
          } catch {
            toast.error('Failed to save template');
          }
        }}
      />

      {/* PR-13 (2026-07-22): the floating Save-as-Template CTA is retired —
          it now lives inside the Estimate rail (see onSaveAsTemplate above). */}

      {/* IDI-280 (2026-09-14): ChooseTCTemplateModal + the T&C info Dialog
          removed — both were only reachable via the T&C card's "Change"
          button, which is now removed. T&C selection happens exclusively on
          Review & Send now. */}

      {/* §2a: Quote Analysis dialog */}
      <Dialog open={quoteAnalysisOpen} onClose={() => setQuoteAnalysisOpen(false)} size="report">
        <DialogHeader onClose={() => setQuoteAnalysisOpen(false)}>Quote Analysis</DialogHeader>
        <DialogBody>
          <QuoteAnalysisReport lineItems={lineItems} summary={effectiveSummary} metadata={metadata} />
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => setQuoteAnalysisOpen(false)}>Close</Button>
          {/* IDI-316 row 1 (2026-09-12): print isolation via .qa-report
              (QuoteAnalysisReport.print.css) confirmed working — probed the
              Dialog's portal-rendered content live before building this;
              it's still a `body *` descendant so the existing .qd-doc-style
              visibility-toggle mechanism applies unchanged, scoped to a new
              class since QuoteAnalysisReport never carried `.qd-doc`. Fix
              re-verified live (built + served locally against the dev API)
              after this exact change was found reverted mid-session — see
              QuoteAnalysisReport.print.css's docblock for the verification
              steps. */}
          <Button onClick={() => window.print()}>Download</Button>
        </DialogFooter>
      </Dialog>

      {/* IDI-105: Material List (area bands, no money, Qty on Hand unclamped). */}
      <MaterialListDialog
        open={materialListOpen}
        onClose={() => setMaterialListOpen(false)}
        quoteId={isTemplateEdit ? null : quoteId}
        lineItems={lineItems}
        catalog={catalogRowsAll}
        accessoryCatalog={catalogRowsAll}
        title={metadata.title}
        canSend={!isTemplateEdit}
      />
    </div>
  );
}
